import { useEffect, useRef, useState, useCallback } from 'react';
import { init, dispose, registerIndicator, registerOverlay, IndicatorSeries, YAxisType, LineType, DomPosition, CandleType, type Chart } from 'klinecharts';
import { useChartStore, SUBPANE_MIN_HEIGHT, CANDLE_MIN_HEIGHT } from '@/store/chartStore';
import { useAuthStore, waitForAuthSettled } from '@/store/authStore';
import { createRsiCompanions } from './rsiCompanions';
import { api } from '@/services/api';
import { buildStyles } from './chartStyles';
import { ChartLegend } from './ChartLegend';
import { AxisValueLabels } from './AxisValueLabels';
import { IndicatorBar } from './IndicatorBar';
import { COMPARE_COLORS_EXPORT } from '@/components/Header/CompareBar';
import { INDICATORS } from './indicatorDefs';
import type { OHLCVBar, IntradayResolution } from '@/types';
import { isIntradayResolution } from '@/types';
import { isCSEMarketOpen } from '@/utils/marketHours';
import { hexToRgba } from '@/utils/color';
import { isIndexSymbol } from '@/utils/symbol';
import { useRSLines } from '@/hooks/useRSLines';
import { useIsMobile } from '@/hooks/useIsMobile';
import { customOverlays } from '@/overlays';
import { hlcBars, rsLine, volLabels, volSpikeStrip, volSpikeTimeline, momentumOverlay, momentumTracker, crossVol, eventMarkers, epsLine, cupHandle, williamOneilSell, rsiZones, rsiDivergence, foreignHolding, pvt, sar, atr } from '@/indicators';
import { eventMarkerLayouts } from '@/indicators/eventMarkers';
import { sellMarkerLayouts, formatSellMarkerDate } from '@/indicators/williamOneilSell';
import { perLines } from '@/indicators/perLines';
import { breadthIndicators } from '@/indicators/breadthIndicators';
import { ensureBreadthData } from '@/store/breadthStore';
import { usePerLines } from '@/hooks/usePerLines';
import { Z_INDEX } from '@/constants/zIndex';
import { useEpsLine } from '@/hooks/useEpsLine';
import { useForeignHolding } from '@/hooks/useForeignHolding';
import { useAlertStore } from '@/store/alertStore';
import { useEvents } from '@/hooks/useEvents';
import { EventTooltip } from './EventTooltip';
import { WilliamOneilSellTooltip } from './WilliamOneilSellTooltip';
import { PeriodBar } from './PeriodBar';
import { cancelTooltipClear, registerTooltipClearFn, scheduleTooltipClear } from './eventTooltipState';
import { cancelSellTooltipClear, registerSellTooltipClearFn, scheduleSellTooltipClear } from './sellTooltipState';
import type { Alert } from '@/types/alerts';
import { useMacroStore } from '@/store/macroStore';
import { registerMacroIndicator, macroIndName, macroPaneId, macroWindowKey, createMacroCalc, buildMacroFigures, buildMacroLineStyles } from '@/indicators/macroOverlay';

/** Apply persisted candle type (including HLC indicator creation). */
function applyPersistedCandleType(chart: Chart) {
  const type = useChartStore.getState().activeCandleType;
  if (type === 'hlc') {
    chart.setStyles({
      candle: { type: CandleType.Area, area: { lineColor: 'rgba(0,0,0,0)', backgroundColor: 'rgba(0,0,0,0)' } },
    });
    try {
      (chart as unknown as { createIndicator: (n: string, s: boolean, o: { id: string }) => void })
        .createIndicator('HLC', true, { id: 'candle_pane' });
    } catch { /* ignore — may already exist */ }
  } else if (type === CandleType.Area) {
    const up = useChartStore.getState().candleUpColor;
    chart.setStyles({
      candle: { type: CandleType.Area, area: { lineColor: up, backgroundColor: hexToRgba(up, 0.15) } },
    });
  } else if (type && type !== CandleType.CandleSolid) {
    chart.setStyles({ candle: { type: type as CandleType } });
  }
}

/** Apply saved VOL bar + MA line colours from indicatorColors store. */
function applyVolStyles(chart: Chart) {
  const colors = useChartStore.getState().indicatorColors['VOL'];
  if (!colors?.length) return;
  const maColors = colors.slice(2);
  const override = {
    name: 'VOL',
    styles: {
      bars: [{
        style: 'fill',
        upColor:       colors[0],
        downColor:     colors[1] ?? colors[0],
        noChangeColor: colors[0],
      }],
      ...(maColors.length ? { lines: maColors.map((color: string) => ({ style: 'solid', smooth: false, size: 1, dashedValue: [2, 2], color })) } : {}),
    } as Record<string, unknown>,
  };
  try {
    (chart as unknown as { overrideIndicator: (o: Record<string, unknown>, paneId?: string) => void })
      .overrideIndicator(override, 'vol_pane');
  } catch { /* ignore */ }
}

/** Apply all persisted chart settings (colours, grid, marks, crosshair, axis) from chartStore. */
function applyPersistedChartSettings(chart: Chart) {
  const s = useChartStore.getState();
  chart.setStyles({
    candle: {
      bar: {
        upColor: s.candleUpColor, upBorderColor: s.candleUpColor, upWickColor: s.candleUpColor,
        downColor: s.candleDownColor, downBorderColor: s.candleDownColor, downWickColor: s.candleDownColor,
      },
      priceMark: {
        show: s.showPriceMarkHigh || s.showPriceMarkLow || s.showLastPriceLine,
        high: { show: s.showPriceMarkHigh },
        low:  { show: s.showPriceMarkLow },
        last: { show: s.showLastPriceLine, line: { show: s.showLastPriceLine } },
      },
    },
    grid: { horizontal: { show: s.showHGrid }, vertical: { show: s.showVGrid } },
    crosshair: { show: s.showCrosshair },
    yAxis: {
      type:
        s.priceScaleType === 'log'        ? YAxisType.Log
        : s.priceScaleType === 'percentage' ? YAxisType.Percentage
        : YAxisType.Normal,
      reverse: s.yAxisReverse,
    } as never,
    indicator: {
      ohlc: { upColor: s.hlcUpColor, downColor: s.hlcDownColor, noChangeColor: '#9598a1' },
      // KLineCharts' native last-value badges have no collision avoidance — multiple lines with
      // close values overlap into unreadable stacked text. Disabled globally; AxisValueLabels
      // renders an equivalent DOM overlay with vertical decluttering instead.
      lastValueMark: { show: false },
    },
  });
}

// Register custom overlays once at module load (idempotent — ignored if already registered)
customOverlays.forEach((overlay) => {
  try { registerOverlay(overlay); } catch { /* already registered */ }
});

// Register custom indicators
// pvt and sar override klinecharts' own built-in entries of the same name —
// see indicators/pvt.ts and indicators/sar.ts for why (both bundled 9.8.12
// calcs have real formula bugs, fixed upstream only in klinecharts v10, a
// breaking major this app hasn't adopted). atr is a new registration — 'ATR'
// was listed in indicatorDefs.ts but never backed by a real indicator.
try { registerIndicator(pvt); }             catch { /* already registered */ }
try { registerIndicator(sar); }             catch { /* already registered */ }
try { registerIndicator(atr); }             catch { /* already registered */ }
try { registerIndicator(hlcBars); }         catch { /* already registered */ }
try { registerIndicator(rsLine); }          catch { /* already registered */ }
try { registerIndicator(perLines); }        catch { /* already registered */ }
try { registerIndicator(volLabels); }       catch { /* already registered */ }
try { registerIndicator(volSpikeStrip); }   catch { /* already registered */ }
try { registerIndicator(volSpikeTimeline); } catch { /* already registered */ }
try { registerIndicator(momentumOverlay); }  catch { /* already registered */ }
try { registerIndicator(momentumTracker); }  catch { /* already registered */ }
try { registerIndicator(crossVol); }         catch { /* already registered */ }
try { registerIndicator(eventMarkers); }     catch { /* already registered */ }
try { registerIndicator(epsLine); }          catch { /* already registered */ }
try { registerIndicator(cupHandle); }        catch { /* already registered */ }
try { registerIndicator(williamOneilSell); } catch { /* already registered */ }
try { registerIndicator(foreignHolding); }   catch { /* already registered */ }
try { registerIndicator(rsiZones); }         catch { /* already registered */ }
try { registerIndicator(rsiDivergence); }    catch { /* already registered */ }
breadthIndicators.forEach((ind) => {
  try { registerIndicator(ind); } catch { /* already registered */ }
});

// KLineChart v9 KLineData shape
interface KLineData {
  timestamp: number; // milliseconds
  open: number;
  high: number;
  low: number;
  close: number;
  volume?: number;
  crossVolume?: number;
}

type VolLabelLayout = {
  key: string;
  x: number;
  y: number;
  w: number;
  h: number;
  closeBox?: { x: number; y: number; w: number; h: number };
};
type VolLabelHit = { layout: VolLabelLayout; paneTop: number; paneLeft: number } | null;

function toKLineData(bars: OHLCVBar[]): KLineData[] {
  return bars.map((b) => {
    const c    = b.close;
    const open = b.open ?? c;
    const high = b.high ?? (open > c ? open : c);
    const low  = b.low  ?? (open < c ? open : c);
    return { timestamp: b.time * 1000, open, high, low, close: c, volume: b.volume ?? 0, crossVolume: b.crossVolume ?? 0 };
  });
}

// ── Read active indicator values from the klinecharts instance ───────────────
// Called on every crosshair change and after indicator toggle, so ChartLegend
// can display live values in React without the canvas tooltip overlapping.
type _IndInstance = {
  name: string;
  calcParams: number[];
  figures: Array<{ key: string; title?: string }>;
  result: Array<Record<string, number | null | undefined>>;
};

function getRightmostVisibleIndex(ch: Chart): number {
  const c = ch as unknown as { getVisibleRange: () => { from: number; to: number } };
  const range = c.getVisibleRange();
  const dataList = (ch as unknown as { getDataList: () => unknown[] }).getDataList();
  return Math.min(range.to, dataList.length - 1);
}

function readIndValues(ch: Chart, dataIndex: number): Record<string, { params: string; entries: Array<{ title: string; value: string }> }> {
  const c = ch as unknown as {
    getIndicatorByPaneId?: () => Map<string, Map<string, _IndInstance>>;
  };
  const allMap = c.getIndicatorByPaneId?.();
  if (!allMap) return {};
  const out: Record<string, { params: string; entries: Array<{ title: string; value: string }> }> = {};
  allMap.forEach((indMap) => {
    indMap.forEach((ind, name) => {
      if (name.startsWith('COMPARE_')) return;
      const idx = dataIndex >= 0 ? dataIndex : Math.max(0, ind.result.length - 1);
      const data = ind.result[idx];
      if (!data) return;
      const entries = ind.figures
        .filter((f) => typeof f.title === 'string' && (f.title as string).length > 0)
        .map((f) => {
          const raw = data[f.key];
          // NaN (e.g. an MA whose period exceeds the bars available — thinly
          // traded or newly listed symbols) would otherwise render as the
          // literal text "NaN" in the legend.
          if (raw == null || !Number.isFinite(Number(raw))) return { title: f.title as string, value: '—' };
          const n = Number(raw);
          const absN = Math.abs(n);
          const total = (data as Record<string, unknown>)['_total'];
          if (typeof total === 'number' && total > 0) {
            // Breadth count fields — show absolute count + percentage of total stocks.
            const pct = (absN / total * 100).toFixed(1);
            const value = Number.isInteger(absN) ? String(absN) : absN.toFixed(2);
            return { title: f.title as string, value: `${value} (${pct}%)` };
          }
          const value = Number.isInteger(n) ? String(n) : n.toFixed(2);
          return { title: f.title as string, value };
        });
      if (entries.length > 0) {
        out[name] = {
          params: ind.calcParams.length > 0 ? `(${ind.calcParams.join(',')})` : '',
          entries,
        };
      }
    });
  });
  return out;
}

export function ChartContainer() {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef     = useRef<Chart | null>(null);
  const volLabelDragRef = useRef<{
    key: string;
    startX: number;
    startY: number;
    origin: { dx: number; dy: number };
    pointerId: number;
  } | null>(null);
  const lastVolHitRef = useRef<VolLabelHit>(null);

  const theme              = useChartStore((s) => s.theme);
  const setCrosshair       = useChartStore((s) => s.setCrosshair);
  const setCrosshairAxisY  = useChartStore((s) => s.setCrosshairAxisY);
  const setLastBar         = useChartStore((s) => s.setLastBar);
  const setChart           = useChartStore((s) => s.setChart);
  const symbol             = useChartStore((s) => s.symbol);
  const timeframe          = useChartStore((s) => s.timeframe);
  const priceAdjustments   = useChartStore((s) => s.priceAdjustments);
  const chartFromDate      = useChartStore((s) => s.chartFromDate);
  const compareSymbols     = useChartStore((s) => s.compareSymbols);
  const priceScaleType     = useChartStore((s) => s.priceScaleType);
  const watchlist          = useChartStore((s) => s.watchlist);
  const setCompareAnchorTs = useChartStore((s) => s.setCompareAnchorTs);
  const setChartWrapperEl  = useChartStore((s) => s.setChartWrapperEl);
  const rightPanelActive   = useChartStore((s) => s.rightPanelActive);
  const rightToolbarOpen   = useChartStore((s) => s.rightToolbarOpen);
  const setCorporateEvents = useChartStore((s) => s.setCorporateEvents);
  const setHoveredEvents   = useChartStore((s) => s.setHoveredEvents);
  // Register the clear fn once so debounced clears reach the store
  useEffect(() => { registerTooltipClearFn(() => setHoveredEvents(null, null)); }, [setHoveredEvents]);
  const setPinnedEvents    = useChartStore((s) => s.setPinnedEvents);
  const setHoveredSellEvents = useChartStore((s) => s.setHoveredSellEvents);
  useEffect(() => { registerSellTooltipClearFn(() => setHoveredSellEvents(null, null)); }, [setHoveredSellEvents]);
  const setPinnedSellEvents  = useChartStore((s) => s.setPinnedSellEvents);
  // Symbol/timeframe changes are usually triggered by clicking the watchlist
  // or search (not a mousemove over the chart canvas), so nothing else clears
  // a stale hover/pin — without this, a tooltip left open on the old chart
  // stays visible, showing markers that no longer correspond to what's drawn.
  useEffect(() => {
    setHoveredEvents(null, null);
    setPinnedEvents(null, null);
    setHoveredSellEvents(null, null);
    setPinnedSellEvents(null, null);
  }, [symbol, timeframe, setHoveredEvents, setPinnedEvents, setHoveredSellEvents, setPinnedSellEvents]);
  const symbolName         = watchlist.find((w) => w.symbol === symbol)?.name ?? '';

  const wrapperRef = useRef<HTMLDivElement>(null);

  // ── Corporate events ────────────────────────────────────────────────────────
  const { data: eventsData } = useEvents();
  useEffect(() => {
    setCorporateEvents(eventsData?.events ?? []);
  }, [eventsData, setCorporateEvents]);
  // Clear events on unmount
  useEffect(() => () => setCorporateEvents([]), [setCorporateEvents]);

  // ── Alert Y-axis hover ──────────────────────────────────────────────────────
  // State: null = hidden; {y, price} = show floating "+" pill at cursor Y
  const [yAxisHoverPrice, setYAxisHoverPrice] = useState<{ y: number; price: number } | null>(null);
  const alertOverlayIdsRef = useRef<Map<string, string[]>>(new Map()); // alertId → overlayId[]
  const { openCreateModal: openAlertModal } = useAlertStore();
  const alerts         = useAlertStore((s) => s.alerts);
  const hiddenAlertIds = useAlertStore((s) => s.hiddenAlertIds);

  // RS Line overlay — manages data loading, indicator lifecycle and window.__rs_lines
  useRSLines(chartRef);

  // Indices (ASI, sector indices, S&P SL20, ...) have no exchange-board suffix
  // and no fundamentals data — /api/fundamentals 400s for them. This is a
  // reliable, fetch-free check to skip the fundamentals-driven overlays
  // below entirely for index symbols.
  const isSymbolIndex = isIndexSymbol(symbol);

  // PER Lines overlay — manages fundamentals data loading and window.__per_lines_data
  usePerLines(symbol, !isSymbolIndex);

  // EPS line overlay — manages fundamentals data loading
  // Only show on Daily and Weekly timeframes (not intraday or monthly)
  const epsLineEnabled = useChartStore((s) => s.epsLineEnabled);
  const epsTimeframeSupported = !isIntradayResolution(timeframe) && timeframe !== '1M';
  useEpsLine(symbol, epsLineEnabled && epsTimeframeSupported && !isSymbolIndex);

  // Foreign Holding sub-pane — driven entirely by whether the indicator is
  // currently on the chart (indicatorPaneMap), no dedicated enabled flag.
  useForeignHolding(symbol);

  // Register wrapper element in store so Header can composite overlays for screenshots
  useEffect(() => {
    setChartWrapperEl(wrapperRef.current);
    return () => setChartWrapperEl(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refs so the load callback always reads current values without re-registering
  const symbolRef    = useRef(symbol);
  const timeframeRef = useRef(timeframe);
  const adjustRef    = useRef(priceAdjustments);
  useEffect(() => { symbolRef.current = symbol; }, [symbol]);
  useEffect(() => { timeframeRef.current = timeframe; }, [timeframe]);
  useEffect(() => { adjustRef.current = priceAdjustments; }, [priceAdjustments]);

  // One div ref per compare symbol — written via direct DOM mutation from the indicator draw callback
  // to position a coloured % pill inside the Y-axis strip each frame (no React state thrashing).
  const compareLabelsRef = useRef<Array<HTMLDivElement | null>>([]);

  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState<string | null>(null);
  const [pricePaneHeight, setPricePaneHeight] = useState<number | null>(null);
  const isMobile = useIsMobile();

  // transparent logos — dark variant has white text (for dark bg), default has dark text (for light bg)
  const logoSrc = theme === 'dark'
    ? '/logo-text-trans-dark-1024.png'
    : '/logo-text-trans-1024.png';
  // outline drop-shadow traces the logo shape (works on transparent PNGs)
  const logoOutline = theme === 'dark'
    ? 'drop-shadow(0 0 1px rgba(255,255,255,0.55)) drop-shadow(0 0 2px rgba(255,255,255,0.25))'
    : 'drop-shadow(0 0 1px rgba(0,0,0,0.55)) drop-shadow(0 0 2px rgba(0,0,0,0.25))';
  // favicon icon for mobile: solid variants read better at small sizes
  const iconSrc = theme === 'dark'
    ? '/logo-dark-128x128.png'
    : '/logo-light-128x128.png';
  const iconOutline = theme === 'dark'
    ? 'drop-shadow(0 0 1px rgba(255,255,255,0.5))'
    : 'drop-shadow(0 0 1px rgba(0,0,0,0.5))';

  // Keep price pane height in sync for watermark + logo positioning
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    type ChartWithSize = Chart & {
      getSize: (paneId: string, position: DomPosition) => { width: number; height: number; top: number } | null;
    };
    function refresh() {
      const c = chartRef.current as ChartWithSize | null;
      if (c) {
        const s = c.getSize('candle_pane', DomPosition.Root);
        if (s) { setPricePaneHeight(s.height); return; }
      }
      setPricePaneHeight(null);
    }

    // Resizes all sub-pane indicator panes in-place using setPaneOptions — no remove/re-add,
    // no flicker, no pane-ID churn. Works correctly for any number of open sub-panes.
    const rebalancePanes = async () => {
      type ChartFull = Chart & {
        getSize: (paneId: string, pos: DomPosition) => { width: number; height: number; top: number } | null;
      };
      const chart = chartRef.current as ChartFull | null;
      if (!chart) return;
      const chartResizable = chart as unknown as { resize?: () => void };

      // Step 1: Sync klinecharts' internal total height to the DOM container.
      // After multiple createIndicator calls the internal total can drift well above
      // wrapper.clientHeight.  When setPaneOptions receives a pixel value klinecharts
      // converts it to a proportion of its INTERNAL total — if that total is 1 200 px
      // but the viewport is 700 px, a setPaneOptions(125) call means 125/1 200 ≈ 10 %,
      // and after the final resize() the pane becomes only ~73 px.  Calling resize()
      // first resets the internal total to wrapper.clientHeight so all subsequent
      // setPaneOptions calls reference the correct baseline.
      //
      // Additionally: createIndicator can inflate containerRef's inline style.height
      // beyond wrapper.clientHeight (e.g., by the new pane's pixel height).  When
      // resize() reads container.clientHeight it would then use this inflated value as
      // the baseline, making all subsequent setPaneOptions proportions wrong and
      // causing the klinecharts canvas to overflow below the chart wrapper — visually
      // hiding the PeriodBar below.  Pinning the container height to wrapper.clientHeight
      // before resize() ensures klinecharts always uses the correct reference height.
      const containerEl = containerRef.current;
      if (containerEl) containerEl.style.height = `${wrapper.clientHeight}px`;
      chartResizable.resize?.();

      const { indicatorPaneMap, hiddenIndicators, paneHeights } = useChartStore.getState();
      const totalH = wrapper.clientHeight;
      const volH   = 80;
      // Hidden (collapsed) sub-panes shrink to a title bar — same size as ChartLegend's hide toggle.
      const collapsedH   = 28;
      const collapsedMin = 24;

      // Collect unique sub-pane IDs (skip VOL and candle-overlay indicators)
      const subPaneIds = new Set<string>();
      const collapsedPaneIds = new Set<string>();
      indicatorPaneMap.forEach((paneId, name) => {
        if (name === 'VOL') return;
        // Macro panes: include only if visible
        if (name.startsWith('MACRO_')) {
          const seriesId = name.slice('MACRO_'.length);
          const pinned = useMacroStore.getState().pinnedSeries.find((s) => s.seriesId === seriesId);
          if (pinned?.visible !== false) { subPaneIds.add(paneId); }
          return;
        }
        const def = INDICATORS.find((i) => i.name === name);
        if (!def || def.paneId) return;
        (hiddenIndicators.has(name) ? collapsedPaneIds : subPaneIds).add(paneId);
      });

      if (subPaneIds.size === 0 && collapsedPaneIds.size === 0) { refresh(); return; }

      // Reserve at least 35% of the container (min 200px) for the candle pane.
      // Distribute the remaining space equally across expanded sub-panes; floor at 30px so
      // even all 10 possible sub-pane indicators fit without overflowing.
      const collapsedTotal = collapsedH * collapsedPaneIds.size;
      const candleMinH = Math.max(200, Math.floor(totalH * 0.35));

      // Panes the user dragged to a specific height keep it (capped so the price
      // pane can't be squeezed below CANDLE_MIN_HEIGHT); the rest share what's left.
      const maxCustomH = Math.max(SUBPANE_MIN_HEIGHT, totalH - volH - CANDLE_MIN_HEIGHT - collapsedTotal);
      const customHeights = new Map<string, number>();
      subPaneIds.forEach((id) => {
        const h = paneHeights[id];
        if (h != null) customHeights.set(id, Math.min(maxCustomH, Math.max(SUBPANE_MIN_HEIGHT, h)));
      });
      let customTotal = 0;
      customHeights.forEach((h) => { customTotal += h; });

      const autoCount  = subPaneIds.size - customHeights.size;
      const available  = Math.max(0, totalH - volH - candleMinH - collapsedTotal - customTotal);
      const paneH = autoCount > 0
        ? Math.max(30, Math.min(150, Math.floor(available / autoCount)))
        : 0;
      const minH  = Math.max(24, Math.floor(paneH * 0.5));

      subPaneIds.forEach((id) => {
        const custom = customHeights.get(id);
        try {
          chart.setPaneOptions(custom != null
            ? { id, height: custom, minHeight: SUBPANE_MIN_HEIGHT }
            : { id, height: paneH, minHeight: minH });
        } catch { /* ignore */ }
      });
      collapsedPaneIds.forEach((id) => {
        try { chart.setPaneOptions({ id, height: collapsedH, minHeight: collapsedMin }); } catch { /* ignore */ }
      });

      // Explicitly set the candle pane so the sum of all panes equals totalH.
      // klinecharts may or may not honour this (it silently ignores unknown IDs)
      // but when it does work it avoids any residual gap at the bottom.
      // Use the actual remainder so the sum of all panes never exceeds totalH.
      // If the remainder drops below 60 px the candle pane becomes small, but
      // that only happens when the user has stacked an extreme number of sub-
      // panes on a short screen — better than overflowing the viewport.
      const candleH = Math.max(60, totalH - volH - paneH * autoCount - customTotal - collapsedTotal);
      try { chart.setPaneOptions({ id: 'candle_pane', height: candleH }); } catch { /* ignore */ }

      // Step 2: Commit — resize() re-reads the DOM and proportionally scales every
      // pane to fit.  Because we called resize() at the start, all proportions now
      // reference the correct totalH, so the final layout matches exactly.
      chartResizable.resize?.();

      // Synchronous call handles live triggers (pane drag, window resize) where
      // klinecharts has already committed getSize() state before the event fires.
      useChartStore.getState().refreshPaneTops();

      // RAF call handles the template-load case: after a programmatic resize(),
      // klinecharts commits updated pane top offsets inside its own internal RAF.
      // Our RAF is queued immediately after resize(), so it fires second (FIFO),
      // reads the correct top values, and the legend icons land in the right place.
      // refresh() stays here (not in the sync call) to preserve the original
      // price-pane-height update timing that keeps panes inside the viewport.
      requestAnimationFrame(() => {
        useChartStore.getState().refreshPaneTops();
        refresh();
      });
    };

    useChartStore.getState().setRefreshPricePaneHeight(refresh);
    useChartStore.getState().setRebalancePanes(rebalancePanes);
    const rafId = requestAnimationFrame(refresh);
    const ro = new ResizeObserver(() => {
      // Tell KLineCharts to re-measure its container — without this the canvas
      // stays at its old size when the right sidebar is toggled open/closed.
      const chart = chartRef.current;
      if (chart) {
        const containerEl = containerRef.current;
        if (containerEl) {
          const wrapperEl = wrapperRef.current;
          if (wrapperEl) containerEl.style.height = `${wrapperEl.clientHeight}px`;
        }
        chart.resize();
      }
      refresh();
    });
    ro.observe(wrapper);

    // Handle mobile browser chrome show/hide (address bar, bottom toolbar) which
    // changes the visual viewport without firing element ResizeObserver.
    const handleVisualViewportResize = () => {
      const chart = chartRef.current;
      const wrapperEl = wrapperRef.current;
      if (!chart || !wrapperEl) return;
      const containerEl = containerRef.current;
      if (containerEl) containerEl.style.height = `${wrapperEl.clientHeight}px`;
      chart.resize();
      requestAnimationFrame(() => {
        const c = chartRef.current;
        if (!c) return;
        c.resize();
        useChartStore.getState().refreshPaneTops();
        useChartStore.getState().refreshPricePaneHeight();
      });
    };
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleVisualViewportResize);
    }
    const handleOrientationChange = () => {
      // iOS can take a moment to settle dimensions after rotation
      setTimeout(handleVisualViewportResize, 150);
    };
    window.addEventListener('orientationchange', handleOrientationChange);

    // Keep logo bottom-left aligned when user drags pane separators
    const handlePaneDrag = () => requestAnimationFrame(refresh);
    type ChartSub = Chart & { subscribeAction: (t: string, cb: () => void) => void; unsubscribeAction: (t: string, cb: () => void) => void };
    const chartForSub = chartRef.current as ChartSub | null;
    chartForSub?.subscribeAction('onPaneDrag', handlePaneDrag);

    return () => {
      cancelAnimationFrame(rafId);
      ro.disconnect();
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleVisualViewportResize);
      }
      window.removeEventListener('orientationchange', handleOrientationChange);
      (chartRef.current as ChartSub | null)?.unsubscribeAction('onPaneDrag', handlePaneDrag);
      useChartStore.getState().setRefreshPricePaneHeight(() => {});
      useChartStore.getState().setRebalancePanes(async () => {});
    };
  }, []);

  // Skip the symbol/timeframe effect on first mount — initial load handled below in chart init
  const firstMountRef = useRef(true);
  // Shared mutable state for the infinite-scroll guards (set inside chart init, reset on symbol change)
  const fetchingMoreRef = useRef(false);
  const hasMoreRef      = useRef(true);
  // Monotonic counter identifying the "current" symbol/timeframe load. Every
  // load (initial chart creation, symbol/timeframe switch) captures its value
  // after incrementing it; every async callback that mutates the chart
  // (applyNewData/applyMoreData) re-checks it before applying, and bails out
  // if a newer load has since superseded it. Without this, a late-resolving
  // response for symbol A (an in-flight infinite-scroll prefetch, or the
  // load itself, delayed by network jitter) can still land after the user
  // has switched to symbol B, splicing A's candles onto B's chart. Mirrors
  // the version-ref pattern already used correctly in hooks/useQuery.ts.
  const loadGenerationRef = useRef(0);
  // Same idea as loadGenerationRef, but scoped to the compare-symbols effect,
  // which has its own independent re-run cycle (deps: compareSymbols,
  // timeframe, priceAdjustments — not the main `symbol`, since compare
  // overlays persist across main-symbol navigation). Without this, removing
  // and re-adding a compare symbol quickly enough lets a late-resolving
  // fetch for the OLD set write into a window.__compare_COMPARE_{idx} slot
  // the NEW set is now using for a different symbol, showing the wrong
  // price in the legend for that row.
  const compareGenerationRef = useRef(0);
  // Live-polling interval handle (60 s, during CSE market hours)
  const liveIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Initialise chart once on mount
  useEffect(() => {
    if (!containerRef.current) return;

    // v9 init: (container, options) — no `layout` option; panes created separately
    const chart = init(containerRef.current, {
      locale:   'en-US',
      timezone: 'Asia/Colombo',
      styles:   buildStyles(theme === 'dark'),
      // Compact formatter used by sub-pane indicators that set shouldFormatBigNumber=true.
      // Price-pane indicators (MA, EMA, BOLL…) never set that flag so are unaffected.
      customApi: {
        formatBigNumber: (value: string | number) => {
          const n = typeof value === 'string' ? parseFloat(value) : value;
          if (isNaN(n)) return String(value);
          const abs = Math.abs(n);
          const sign = n < 0 ? '-' : '';
          if (abs >= 1_000_000_000_000) return sign + (abs / 1_000_000_000_000).toFixed(2) + 'Tr';
          if (abs >= 1_000_000_000)     return sign + (abs / 1_000_000_000).toFixed(2) + 'Bn';
          if (abs >= 1_000_000)         return sign + (abs / 1_000_000).toFixed(2) + 'Mn';
          if (abs >= 1_000)             return sign + (abs / 1_000).toFixed(1) + 'K';
          return n.toFixed(2);
        },
      },
    }) as Chart | null;

    if (chart) {
      // klinecharts hardcodes tabIndex=1 on its internal chart container
      // (see node_modules/klinecharts — `this._chartContainer.tabIndex = 1`),
      // which fails the "no positive tabindex" accessibility/agent-browsing
      // check. 0 keeps it in the natural tab order without the anti-pattern.
      const internalContainer = containerRef.current?.querySelector<HTMLElement>('[tabindex="1"]');
      if (internalContainer) internalContainer.tabIndex = 0;

      // Create the default volume sub-pane
      (chart as unknown as {
        createIndicator: (name: string, isStack: boolean, paneOptions: { id: string; height: number; minHeight: number }) => string;
      }).createIndicator('VOL', false, { id: 'vol_pane', height: 80, minHeight: 60 });

      // Override VOL calc: when crossVolumeEnabled is off, subtract crossVolume from each bar's
      // effective volume before computing MA lines so both bars and MAs reflect net retail volume.
      // The built-in VOL figures (volume bar + ma5/ma10 lines) are preserved — only calc changes.
      // Toggling calls overrideIndicator({name:'VOL'}, 'vol_pane') which re-runs calc instantly.
      (chart as unknown as {
        overrideIndicator: (o: Record<string, unknown>, paneId: string) => void;
      }).overrideIndicator({
        name: 'VOL',
        shouldFormatBigNumber: true,
        calc: (dataList: KLineData[], indicator: { calcParams?: number[] }) => {
          const { crossVolumeEnabled } = useChartStore.getState();
          const periods = indicator.calcParams?.length ? indicator.calcParams : [5, 10];
          const effVols = dataList.map((bar) =>
            crossVolumeEnabled
              ? (bar.volume ?? 0)
              : Math.max(0, (bar.volume ?? 0) - (bar.crossVolume ?? 0)),
          );
          return dataList.map((_, i) => {
            const row: Record<string, number> = { volume: effVols[i] };
            periods.forEach((p, idx) => {
              if (i >= p - 1) {
                let sum = 0;
                for (let k = i - p + 1; k <= i; k++) sum += effVols[k];
                row[`ma${idx + 1}`] = sum / p;
              }
            });
            return row;
          });
        },
      }, 'vol_pane');

      // Re-apply saved VOL colours (VOL is excluded from activeIndicatorNames so the
      // restoration loop below skips it — we must restore its bar/line colours here).
      applyVolStyles(chart);

      // Companion indicator that draws scan-label annotations inside vol_pane.
      // Always stacked — draws nothing when no rules are configured.
      (chart as unknown as {
        createIndicator: (name: string, isStack: boolean, paneOptions: { id: string }) => string;
      }).createIndicator('VOL_LABELS', true, { id: 'vol_pane' });

      // Crossing volume overlay — always created; visibility gated by crossVolumeEnabled store bool.
      (chart as unknown as {
        createIndicator: (name: string, isStack: boolean, paneOptions: { id: string }) => string;
      }).createIndicator('CROSS_VOL', true, { id: 'vol_pane' });

      // Event markers — draws corporate event triangles at bottom of candle pane.
      // Always stacked in candle_pane; visibility gated by showEvents store bool.
      (chart as unknown as {
        createIndicator: (name: string, isStack: boolean, paneOptions: { id: string }) => string;
      }).createIndicator('EVENT_MARKERS', true, { id: 'candle_pane' });

      // Subscribe to crosshair changes
      (chart as unknown as {
        subscribeAction: (type: string, cb: (d: unknown) => void) => void;
      }).subscribeAction('onCrosshairChange', (rawData: unknown) => {
        const d = rawData as { kLineData?: KLineData; dataIndex?: number; y?: number; paneId?: string } | null;
        if (d?.kLineData) {
          const k = d.kLineData;
          setCrosshair({
            time:   Math.floor((k.timestamp ?? 0) / 1000),
            open:   k.open,
            high:   k.high,
            low:    k.low,
            close:  k.close,
            volume: k.volume ?? 0,
          });
          useChartStore.getState().setIndicatorValues(readIndValues(chart, d.dataIndex ?? 0));
          setCrosshairAxisY(
            typeof d.y === 'number' && typeof d.paneId === 'string' ? { paneId: d.paneId, y: d.y } : null
          );
        } else {
          setCrosshair(null);
          setCrosshairAxisY(null);
          useChartStore.getState().setIndicatorValues(readIndValues(chart, getRightmostVisibleIndex(chart)));
        }
      });

      // Subscribe to visible range changes so indicator values follow the rightmost visible bar when panning
      (chart as unknown as {
        subscribeAction: (type: string, cb: () => void) => void;
      }).subscribeAction('onVisibleRangeChange', () => {
        if (!useChartStore.getState().crosshair) {
          useChartStore.getState().setIndicatorValues(readIndValues(chart, getRightmostVisibleIndex(chart)));
        }
      });

      // Snapshot the persisted indicator list now, before any hook (e.g.
      // useRSLines) can react to the new chart. Those hooks rewrite
      // indicatorPaneMap, which also rewrites the persisted
      // activeIndicatorNames — so reading it later, after the first bars
      // arrive, can see a list that has already collapsed to just RS_LINE.
      const bootIndicatorNames = useChartStore.getState().activeIndicatorNames;

      chartRef.current = chart;
      setChart(chart);

      // Apply persisted user settings (colours, grid, marks, etc.)
      applyPersistedChartSettings(chart);
      applyPersistedCandleType(chart);

      // ── Infinite-scroll state (scoped to this chart instance) ──────────────
      let fetchingMore = false; // one request in flight at a time
      let hasMore      = true;  // false once BFF returns < 500 bars

      type ChartFull = Chart & {
        applyMoreData: (bars: KLineData[], more: boolean) => void;
        getDataList:   () => KLineData[];
        getVisibleRange: () => { from: number; to: number; realFrom: number; realTo: number };
      };
      const c = chart as unknown as ChartFull;

      // Keep refs in sync so the symbol/timeframe effect can reset the guards
      const syncRefs = () => {
        fetchingMoreRef.current = fetchingMore;
        hasMoreRef.current      = hasMore;
      };

      // Prefetch next batch when the oldest visible bar is within PREFETCH_THRESHOLD
      // bars of the leftmost loaded bar.
      const PREFETCH_THRESHOLD = 10;

      (chart as unknown as {
        subscribeAction: (type: string, cb: () => void) => void;
      }).subscribeAction('onVisibleRangeChange', () => {
        // Pick up resets triggered by symbol/timeframe change
        fetchingMore = fetchingMoreRef.current;
        hasMore      = hasMoreRef.current;

        if (fetchingMore || !hasMore) return;
        const visible = c.getVisibleRange();
        if (visible.from > PREFETCH_THRESHOLD) return; // still far from left edge

        const dataList = c.getDataList();
        if (!dataList.length) return;

        fetchingMore = true;
        syncRefs();
        const prefetchGen = loadGenerationRef.current;
        const before = Math.floor(dataList[0].timestamp / 1000);
        const loadMore = isIntradayResolution(timeframeRef.current)
          ? api.intraday(symbolRef.current, timeframeRef.current as IntradayResolution, 500, before)
          : api.ohlcv(symbolRef.current, timeframeRef.current, 500, before, undefined, adjustRef.current);
        loadMore
          .then((res) => {
            // A symbol/timeframe switch superseded this prefetch while it was
            // in flight — applying it now would splice the wrong symbol's
            // historical candles onto the chart.
            if (prefetchGen !== loadGenerationRef.current) return;
            hasMore = res.data.length === 500;
            c.applyMoreData(toKLineData(res.data), hasMore);
          })
          .catch(() => { /* silently ignore; user can scroll again to retry */ })
          .finally(() => { fetchingMore = false; syncRefs(); });
      });

      // ── Initial data load ──────────────────────────────────────────────────
      // Use the persisted timeframe — must call intraday API for intraday resolutions.
      setLoading(true);
      const initSym          = symbolRef.current;
      const initTf          = timeframeRef.current;
      const isInitIntraday  = isIntradayResolution(initTf);
      // Rare but possible: the symbol/timeframe switch effect can run (and
      // issue its own load) before this initial load resolves, if the user
      // switches symbols within the first render. Guard against this initial
      // load then overwriting the newer one's data.
      const initGen         = ++loadGenerationRef.current;
      const doInitLoad      = () =>
        isInitIntraday
          ? api.intraday(initSym, initTf as IntradayResolution, 500)
          : api.ohlcv(initSym, initTf, 500, undefined, undefined, adjustRef.current);

      doInitLoad()
        .then(async (res) => {
          if (initGen !== loadGenerationRef.current) return;
          chart.applyNewData(toKLineData(res.data));
          useChartStore.getState().setLoadedSymbol(initSym);
          // Apply persisted price scale type after data is loaded (effect fires before chart exists)
          {
            const { priceScaleType: sc, compareSymbols: cmp } = useChartStore.getState();
            if (cmp.length === 0) {
              const scaleType = sc === 'log' ? YAxisType.Log : sc === 'percentage' ? YAxisType.Percentage : YAxisType.Normal;
              chart.setStyles({ yAxis: { type: scaleType } });
            }
          }
          try {
            (chart as unknown as { overrideIndicator: (o: { name: string }, paneId?: string) => void })
              .overrideIndicator({ name: 'VOL_LABELS' }, 'vol_pane');
          } catch { /* ignore */ }
          setLoading(false);
          requestAnimationFrame(() => { chart.scrollToRealTime(); });
          requestAnimationFrame(() => { useChartStore.getState().setIndicatorValues(readIndValues(chart, getRightmostVisibleIndex(chart))); });

          // ── Restore user indicators after hard refresh ────────────────────
          // overrideIndicator must run AFTER applyNewData so calc() has populated
          // result[] and drawImp won't crash accessing undefined entries.
          // The plan check below needs to know who the user is. This restore
          // runs as soon as the first bars arrive, usually before /api/auth/me
          // has answered — without waiting, a Pro user looks like a guest and
          // their advanced indicators are stripped from the persisted list
          // (permanently, since that list is what gets restored next time).
          await waitForAuthSettled();

          const { indicatorParams, indicatorColors } = useChartStore.getState();
          const persistedIndicatorNames = bootIndicatorNames;

          // Advanced indicators are Pro/Business only. A locally-persisted chart
          // (e.g. from before a downgrade) may still list them in
          // activeIndicatorNames — drop those here instead of silently restoring
          // Pro features for a basic-plan account.
          const restoreUser = useAuthStore.getState().user;
          const hasAdvancedAccess = restoreUser?.plan === 'pro' || restoreUser?.plan === 'business';
          const disallowed = persistedIndicatorNames.filter(
            (n) => !hasAdvancedAccess && INDICATORS.find((i) => i.name === n)?.category === 'advanced'
          );
          disallowed.forEach((n) => useChartStore.getState().removeActiveIndicator(n));

          // Repair state left behind by the plan-check race fixed above: Cup &
          // Handle and O'Neil Sell have no hook that redraws them from their
          // setting, so their "enabled" flag could stay on while the indicator
          // itself was dropped from the list. The toggles always set/clear both
          // together, so for a user entitled to them the flag is the truth.
          const healed: string[] = [];
          if (hasAdvancedAccess) {
            const flags = useChartStore.getState();
            if (flags.cupHandleEnabled && !persistedIndicatorNames.includes('CUP_HANDLE')) healed.push('CUP_HANDLE');
            if (flags.williamOneilSellEnabled && !persistedIndicatorNames.includes('WILLIAM_ONEIL_SELL')) healed.push('WILLIAM_ONEIL_SELL');
          }
          const activeIndicatorNames = [...persistedIndicatorNames.filter((n) => !disallowed.includes(n)), ...healed];

          if (activeIndicatorNames.length > 0) {
            type ChartWithInd = Chart & {
              createIndicator: (name: string, isStack: boolean, opts?: { id?: string; height?: number; minHeight?: number }) => string;
              overrideIndicator: (o: { name: string; calcParams?: number[]; styles?: Record<string, unknown> }, paneId?: string) => void;
            };
            const c2 = chart as unknown as ChartWithInd;
            const restoredMap = new Map<string, string>([['VOL', 'vol_pane']]);

            // Ensure breadth data is ready BEFORE calling createIndicator for advanced indicators.
            // With localStorage persistence this is instant (cache hit). On first visit it awaits
            // the API. Either way calc() has real data when KLineCharts first calls draw().
            const hasAdvanced = activeIndicatorNames.some(
              (n) => INDICATORS.find((i) => i.name === n)?.category === 'advanced'
            );
            if (hasAdvanced) await ensureBreadthData();

            for (const indName of activeIndicatorNames) {
              const def = INDICATORS.find((i) => i.name === indName);
              if (!def) continue;
              // MOMENTUM_TRACKER is activated as a pair with MOMENTUM_OVERLAY;
              // skip here — it is created implicitly when MOMENTUM_OVERLAY is created below.
              if (indName === 'MOMENTUM_TRACKER') continue;
              try {
                const opts = def.paneId ? { id: def.paneId } : { height: def.paneHeight ?? 100, minHeight: 60 };
                const returnedId = c2.createIndicator(indName, !!def.paneId, opts);
                // When restoring MOMENTUM_OVERLAY also restore the tracker sub-pane
                if (indName === 'MOMENTUM_OVERLAY' && activeIndicatorNames.includes('MOMENTUM_TRACKER')) {
                  const trackerDef = INDICATORS.find((i) => i.name === 'MOMENTUM_TRACKER');
                  const trackerPaneId = c2.createIndicator('MOMENTUM_TRACKER', false, {
                    height: trackerDef?.paneHeight ?? 80, minHeight: 60,
                  });
                  restoredMap.set('MOMENTUM_TRACKER', trackerPaneId ?? 'momentum_tracker_pane');
                }
                const resolvedId = def.paneId ?? returnedId ?? indName;
                restoredMap.set(indName, resolvedId);
                if (indName === 'RSI') createRsiCompanions(chart, resolvedId);
                const params = indicatorParams[indName];
                const colors = indicatorColors[indName];
                const override: Record<string, unknown> = { name: indName };
                if (params?.length) override.calcParams = params;
                if (colors?.length) {
                  if (indName === 'VOL') {
                    const maColors = colors.slice(2);
                    override.styles = {
                      bars: [{
                        style: 'fill',
                        upColor:       colors[0],
                        downColor:     colors[1] ?? colors[0],
                        noChangeColor: colors[0],
                      }],
                      ...(maColors.length ? { lines: maColors.map((color: string) => ({ style: 'solid', smooth: false, size: 1, dashedValue: [2, 2], color })) } : {}),
                    };
                  } else {
                    override.styles = {
                      lines: colors.map((color: string) => ({ style: 'solid', smooth: false, size: 1, dashedValue: [2, 2], color })),
                    };
                  }
                  if (def.paneId === 'candle_pane') (override.styles as Record<string, unknown>).tooltip = { showRule: 'none' };
                }
                // Enable compact Y-axis formatting for sub-pane indicators (not price overlays).
                if (def.paneId !== 'candle_pane') override.shouldFormatBigNumber = true;
                c2.overrideIndicator(override as never, resolvedId);
              } catch { /* ignore — indicator may not be registered */ }
            }
            // Keep anything registered while the restore was in flight (macro
            // panes, or RS_LINE added by useRSLines) — replacing the map
            // wholesale would drop it from the pane map and the persisted list.
            const currentMap = useChartStore.getState().indicatorPaneMap;
            currentMap.forEach((paneId, name) => {
              if (!restoredMap.has(name)) restoredMap.set(name, paneId);
            });
            useChartStore.getState().setIndicatorPaneMap(restoredMap);
            // Rebalance pane heights so nothing overflows the container on restore
            void useChartStore.getState().rebalancePanes();
          }
          const lb = res.data.at(-1);
          if (lb) setLastBar({ time: lb.time, open: lb.open, high: lb.high, low: lb.low, close: lb.close, volume: lb.volume ?? 0 });

          // Kick off live-polling if market is open (mirrors symbol/timeframe change effect)
          if (isCSEMarketOpen()) {
            let lastBarTs = res.data.at(-1)?.time ?? 0;
            liveIntervalRef.current = setInterval(async () => {
              try {
                // If the symbol/timeframe has since moved on, this interval is an orphan
                // (superseded by the symbol/timeframe-change effect's own interval) — no-op.
                if (initSym !== symbolRef.current || initTf !== timeframeRef.current) return;
                const liveRes = isIntradayResolution(timeframeRef.current)
                  ? await api.intraday(symbolRef.current, timeframeRef.current as IntradayResolution, 60)
                  : await api.ohlcv(symbolRef.current, timeframeRef.current, 60, undefined, undefined, adjustRef.current);
                if (initSym !== symbolRef.current || initTf !== timeframeRef.current) return;
                const updatedBars = liveRes.data.filter((b) => b.time >= lastBarTs);
                updatedBars.forEach((b) => {
                  (chart as unknown as { updateData: (d: KLineData) => void })
                    .updateData(toKLineData([b])[0]);
                });
                if (updatedBars.length > 0) {
                  lastBarTs = updatedBars[updatedBars.length - 1].time;
                  const latest = updatedBars[updatedBars.length - 1];
                  setLastBar({ time: latest.time, open: latest.open, high: latest.high, low: latest.low, close: latest.close, volume: latest.volume ?? 0 });
                  try {
                    (chart as unknown as { overrideIndicator: (o: { name: string }, paneId?: string) => void })
                      .overrideIndicator({ name: 'VOL_LABELS' }, 'vol_pane');
                  } catch { /* ignore */ }
                }
              } catch { /* silently ignore; next tick will retry */ }
            }, 60_000);
          }
        })
        .catch((err: unknown) => {
          if (initGen !== loadGenerationRef.current) return;
          setError(err instanceof Error ? err.message : 'Failed to load data');
          setLoading(false);
        })
        // Runs after the async restore above has fully finished (or been skipped).
        .finally(() => useChartStore.getState().setChartInitialized(true));
    }

    // ── Shared internal chart type used by the event handlers below ──────────
    type YAxisComp = {
      getAutoCalcTickFlag: () => boolean;
      getRange:  () => { from: number; to: number; range: number; realFrom: number; realTo: number; realRange: number } | null;
      setRange:  (r: { from: number; to: number; range: number; realFrom: number; realTo: number; realRange: number }) => void;
      convertToRealValue: (value: number) => number;
    };
    type InternalChart = {
      _candlePane?: { getAxisComponent: () => YAxisComp };
      adjustPaneViewport: (a: boolean, b: boolean, c: boolean, d: boolean) => void;
    };
    type ChartWithSize = Chart & {
      getSize: (paneId: string, position: DomPosition) => { width: number; height: number; top: number; left?: number } | null;
      overrideIndicator: (o: { name: string }, paneId?: string) => void;
    };

    // ── Enable vertical canvas drag ──────────────────────────────────────────
    // klinecharts only allows vertical panning on the main canvas when the
    // Y axis _autoCalcTickFlag is false (i.e. the Y axis is manually locked).
    // Lock the current auto-scaled range the moment the user presses down so
    // vertical drag works immediately. Double-clicking the Y axis or pressing
    // "Reset View" in the toolbar restores full auto-scale.
    const onCandlePanePointerDown = () => {
      const yAxis = (chart as unknown as InternalChart)?._candlePane?.getAxisComponent();
      if (yAxis?.getAutoCalcTickFlag()) {
        const range = yAxis.getRange();
        if (range) yAxis.setRange({ ...range });
      }
    };
    containerRef.current.addEventListener('pointerdown', onCandlePanePointerDown, { capture: true });

    // ── Volume label drag + dismiss ───────────────────────────────────────
    // Inject a stylesheet so we can override klinecharts' canvas cursor via
    // !important (body / container inline style can't beat child inline styles).
    const VOL_CURSOR_CLASS = '__vol-cursor-hover';
    const cursorStyleEl = document.createElement('style');
    cursorStyleEl.textContent =
      `.${VOL_CURSOR_CLASS} canvas, .${VOL_CURSOR_CLASS} > * { cursor: grab !important; }` +
      `.${VOL_CURSOR_CLASS}.dragging canvas, .${VOL_CURSOR_CLASS}.dragging > * { cursor: grabbing !important; }`;
    document.head.appendChild(cursorStyleEl);
    const setVolCursor = (on: boolean) => {
      containerRef.current?.classList.toggle(VOL_CURSOR_CLASS, on);
    };

    const getVolLabelLayouts = () =>
      ((window as unknown as Record<string, unknown>).__vol_label_layout as VolLabelLayout[] | undefined) ?? [];

    const hitTestLabel = (x: number, y: number) => {
      const c = chartRef.current as ChartWithSize | null;
      if (!c) return null;
      const pane = c.getSize('vol_pane', DomPosition.Root);
      if (!pane) return null;
      const paneLeft = pane.left ?? 0;
      const paneTop  = pane.top;
      const layouts  = getVolLabelLayouts();
      for (let i = layouts.length - 1; i >= 0; i--) {
        const l = layouts[i];
        const x0 = l.x + paneLeft;
        const y0 = l.y + paneTop;
        if (x >= x0 && x <= x0 + l.w && y >= y0 && y <= y0 + l.h) {
          return { layout: l, boxX: x0, boxY: y0, paneTop, paneLeft };
        }
      }
      return null;
    };

    const removeVolLabel = (key: string) => {
      useChartStore.getState().setVolumeLabelHidden(key, true);
      // redraw triggered by the Zustand subscription below
    };

    const onVolLabelPointerDown = (e: PointerEvent) => {
      const container = containerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      // Fall back to the last cached hover hit to avoid timing gaps between
      // the chart draw (which sets __vol_label_layout) and the pointerdown event.
      const hit = hitTestLabel(x, y) ?? lastVolHitRef.current;
      if (!hit) return;

      const { layout, paneLeft, paneTop } = hit;
      const close = layout.closeBox;
      const isCloseHit = close
        ? x >= close.x + paneLeft && x <= close.x + paneLeft + close.w
          && y >= close.y + paneTop && y <= close.y + paneTop + close.h
        : false;
      const altPressed = e.altKey || e.getModifierState?.('Alt');

      if (altPressed || isCloseHit) {
        removeVolLabel(layout.key);
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      const existing = useChartStore.getState().volumeLabelOffsets?.[layout.key] ?? { dx: 0, dy: 0 };
      if (existing.dx !== 0) {
        useChartStore.getState().setVolumeLabelOffset(layout.key, { dx: 0, dy: existing.dy });
      }
      volLabelDragRef.current = {
        key: layout.key,
        startX: x,
        startY: y,
        origin: { dx: 0, dy: existing.dy },
        pointerId: e.pointerId,
      };
      container.setPointerCapture(e.pointerId);
      containerRef.current?.classList.add('dragging');
      e.preventDefault();
      e.stopPropagation();
    };

    const onVolLabelPointerMove = (e: PointerEvent) => {
      const container = containerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const hit = hitTestLabel(x, y);
      lastVolHitRef.current = hit;
      setVolCursor(!!hit);

      const drag = volLabelDragRef.current;
      if (!drag || drag.pointerId !== e.pointerId) return;
      const dy = y - drag.startY;
      useChartStore.getState().setVolumeLabelOffset(drag.key, { dx: 0, dy: drag.origin.dy + dy });
      // redraw triggered by the Zustand subscription below
      e.preventDefault();
    };

    const onVolLabelPointerUp = (e: PointerEvent) => {
      const drag = volLabelDragRef.current;
      const container = containerRef.current;
      if (!container) return;
      setVolCursor(false);

      // Always release capture unconditionally — it's a no-op if not held,
      // and this prevents capture leaking when drag state is out of sync.
      try { container.releasePointerCapture(e.pointerId); } catch { /* ignore */ }

      if (!drag || drag.pointerId !== e.pointerId) {
        const rect = container.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        const hit = hitTestLabel(x, y);
        const altPressed = e.altKey || e.getModifierState?.('Alt');
        if (hit && altPressed) {
          removeVolLabel(hit.layout.key);
          e.preventDefault();
          e.stopPropagation();
        }
        return;
      }

      volLabelDragRef.current = null;
      containerRef.current?.classList.remove('dragging');
    };

    const onVolLabelPointerLeave = (e: PointerEvent) => {
      setVolCursor(false);
      containerRef.current?.classList.remove('dragging');
      // Only cancel a drag when the pointer truly exits the chart container,
      // not when crossing between internal child elements (canvases, separators).
      // With { capture: true } this handler fires for *any* descendant's
      // pointerleave — check relatedTarget to distinguish the two cases.
      if (volLabelDragRef.current) {
        const related = e.relatedTarget as Node | null;
        const stillInside = related && containerRef.current?.contains(related);
        if (!stillInside) {
          volLabelDragRef.current = null;
          try { containerRef.current?.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
        }
      }
    };
    containerRef.current.addEventListener('pointerdown', onVolLabelPointerDown, { capture: true });
    containerRef.current.addEventListener('pointermove', onVolLabelPointerMove, { capture: true });
    containerRef.current.addEventListener('pointerup', onVolLabelPointerUp, { capture: true });
    containerRef.current.addEventListener('pointercancel', onVolLabelPointerUp, { capture: true });
    containerRef.current.addEventListener('pointerleave', onVolLabelPointerLeave as EventListener, { capture: true });

    // ── Vertical Y-axis zoom via mouse wheel ─────────────────────────────────
    // klinecharts' built-in mouseWheelVertEvent always routes vertical scroll to
    // X-axis zoom. We intercept wheel events and redirect them to Y-axis zoom
    // when the cursor is over the right-side Y axis area (≤70 px from edge)
    // or the user holds Ctrl / ⌘. The rest pass through to klinecharts.
    const Y_AXIS_HIT_WIDTH = 70;
    const onWheel = (e: WheelEvent) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const overYAxis = e.clientX > rect.right - Y_AXIS_HIT_WIDTH;

      // ── EPS left axis zoom / pan ────────────────────────────────────────
      // When the cursor is over the left EPS axis strip and EPS_LINE is active,
      // adjust the EPS Y range independently of the price axis.
      //   Plain scroll   → zoom (narrow/widen the range)
      //   Shift + scroll → pan (shift the range centre up/down)
      // Hit-test using the axis canvas bounds published each frame by epsLine draw().
      const epsActive = useChartStore.getState().epsLineEnabled
        && !isIntradayResolution(useChartStore.getState().timeframe)
        && useChartStore.getState().timeframe !== '1M';
      const epsBounds = window.__eps_axis_bounds;
      // epsBounds are canvas-relative; rect.left/top are the container's client offsets.
      const overEpsAxis = epsActive && epsBounds != null
        && e.clientX >= rect.left + epsBounds.left
        && e.clientX <= rect.left + epsBounds.right
        && e.clientY >= rect.top  + epsBounds.top
        && e.clientY <= rect.top  + epsBounds.bottom;
      if (overEpsAxis) {
        e.preventDefault();
        e.stopPropagation();
        type ChartScroll2 = Chart & { scrollByDistance: (d: number, a?: boolean) => void };
        if (e.shiftKey) {
          // Shift+scroll → pan up/down
          // Scale the pan step by the current zoom so it feels consistent at all zoom levels
          const zoom = window.__eps_line_axis_zoom ?? 1;
          const axisHeight = (epsBounds.bottom - epsBounds.top) || 1;
          // panDelta in EPS units: 2/zoom EPS per full axis height per scroll step
          const panDelta = (e.deltaY / axisHeight) * (2 / zoom);
          window.__eps_line_axis_pan = (window.__eps_line_axis_pan ?? 0) + panDelta;
        } else {
          // Plain scroll → zoom
          const factor = window.__eps_line_axis_zoom ?? 1;
          // Scroll up (deltaY < 0) = zoom in; scroll down = zoom out
          const next = factor * (e.deltaY < 0 ? 1.15 : 1 / 1.15);
          window.__eps_line_axis_zoom = Math.max(0.15, Math.min(10, next));
        }
        // Trigger redraw via scrollByDistance(0) — more reliable than overrideIndicator alone
        try {
          (chartRef.current as unknown as ChartScroll2).scrollByDistance(0, false);
        } catch { /* ignore */ }
        return;
      }

      if (!overYAxis && !e.ctrlKey && !e.metaKey) return;

      e.preventDefault();
      e.stopPropagation();

      const ic = (chartRef.current as unknown as InternalChart);
      const yAxis = ic?._candlePane?.getAxisComponent();
      if (!yAxis) return;

      // Lock range first if still in auto-scale mode
      if (yAxis.getAutoCalcTickFlag()) {
        const r = yAxis.getRange();
        if (r) yAxis.setRange({ ...r });
      }

      const r = yAxis.getRange();
      if (!r) return;

      // Zoom in (scroll up / deltaY < 0) → scale < 1, makes the range smaller
      const scale  = e.deltaY > 0 ? 1.1 : 1 / 1.1;
      const midC   = (r.from + r.to) / 2;       // midpoint in axis-coord space
      const midR   = (r.realFrom + r.realTo) / 2; // midpoint in price space
      const halfC  = (r.range * scale) / 2;
      const halfR  = (r.realRange * scale) / 2;

      yAxis.setRange({
        from:      midC - halfC,
        to:        midC + halfC,
        range:     r.range * scale,
        realFrom:  midR - halfR,
        realTo:    midR + halfR,
        realRange: r.realRange * scale,
      });
      // Force klinecharts to repaint the pane after the range changes
      ic.adjustPaneViewport(false, true, true, true);
    };
    containerRef.current.addEventListener('wheel', onWheel, { capture: true, passive: false });

    // ── Vertical Y-axis zoom via touch drag (mobile) ─────────────────────────
    // There's no touch equivalent of a mouse wheel, and klinecharts' own pinch
    // gesture is hardwired to X-axis (time) zoom only — see pinchEvent in
    // klinecharts' Event class. So a single-finger vertical drag that starts
    // within the Y-axis strip (same Y_AXIS_HIT_WIDTH region as onWheel above)
    // is mapped to the same range-rescale math, applied incrementally per move.
    let yAxisTouchActive = false;
    let yAxisTouchLastY = 0;
    const onYAxisTouchStart = (e: TouchEvent) => {
      if (e.touches.length !== 1 || !containerRef.current) return;
      const t = e.touches[0];
      const rect = containerRef.current.getBoundingClientRect();
      if (t.clientX <= rect.right - Y_AXIS_HIT_WIDTH) return;
      yAxisTouchActive = true;
      yAxisTouchLastY = t.clientY;
    };
    const onYAxisTouchMove = (e: TouchEvent) => {
      if (!yAxisTouchActive || e.touches.length !== 1) return;
      const t = e.touches[0];
      const deltaY = t.clientY - yAxisTouchLastY;
      yAxisTouchLastY = t.clientY;
      if (deltaY === 0) return;
      e.preventDefault();
      e.stopPropagation();

      const ic = (chartRef.current as unknown as InternalChart);
      const yAxis = ic?._candlePane?.getAxisComponent();
      if (!yAxis) return;

      if (yAxis.getAutoCalcTickFlag()) {
        const r0 = yAxis.getRange();
        if (r0) yAxis.setRange({ ...r0 });
      }

      const r = yAxis.getRange();
      if (!r) return;

      // Drag down → widen the range (zoom out); drag up → zoom in.
      const scale  = Math.max(0.9, Math.min(1.1, 1 + deltaY * 0.006));
      const midC   = (r.from + r.to) / 2;
      const midR   = (r.realFrom + r.realTo) / 2;
      const halfC  = (r.range * scale) / 2;
      const halfR  = (r.realRange * scale) / 2;

      yAxis.setRange({
        from:      midC - halfC,
        to:        midC + halfC,
        range:     r.range * scale,
        realFrom:  midR - halfR,
        realTo:    midR + halfR,
        realRange: r.realRange * scale,
      });
      ic.adjustPaneViewport(false, true, true, true);
    };
    const onYAxisTouchEnd = () => { yAxisTouchActive = false; };
    containerRef.current.addEventListener('touchstart', onYAxisTouchStart, { capture: true, passive: true });
    containerRef.current.addEventListener('touchmove', onYAxisTouchMove, { capture: true, passive: false });
    containerRef.current.addEventListener('touchend', onYAxisTouchEnd, { capture: true });
    containerRef.current.addEventListener('touchcancel', onYAxisTouchEnd, { capture: true });

    // ── Vertical pan via touch drag on the price pane body ───────────────────
    // klinecharts' touch pipeline for the MAIN pane (Event.prototype.touchMoveEvent
    // in klinecharts' source) never calls the Y-axis pan branch that mouse drag
    // uses (Event.prototype.pressedMouseMoveEvent) — on touch it only ever
    // scrolls the X (time) axis. Reimplement the same pixel→range math here,
    // relative to a fixed drag-start snapshot (not per-frame deltas) to match
    // klinecharts' own approach and avoid drift.
    // Direction is decided once, after a small Manhattan-distance threshold
    // (mirroring klinecharts' own touch disambiguation), so a mostly-horizontal
    // drag is left completely alone — klinecharts' native X-axis scroll for it
    // keeps working exactly as before.
    let panTouch: {
      startX: number;
      startY: number;
      startRange: { from: number; to: number; range: number; realFrom: number; realTo: number; realRange: number };
      direction: 'pending' | 'vertical' | 'horizontal';
    } | null = null;

    const onPricePanTouchStart = (e: TouchEvent) => {
      if (e.touches.length !== 1 || !containerRef.current || volLabelDragRef.current) return;
      const t = e.touches[0];
      const rect = containerRef.current.getBoundingClientRect();
      if (t.clientX > rect.right - Y_AXIS_HIT_WIDTH) return; // that region is owned by the zoom-strip gesture above

      const ic = (chartRef.current as unknown as InternalChart);
      const yAxis = ic?._candlePane?.getAxisComponent();
      if (!yAxis) return;
      if (yAxis.getAutoCalcTickFlag()) {
        const r0 = yAxis.getRange();
        if (r0) yAxis.setRange({ ...r0 });
      }
      const startRange = yAxis.getRange();
      if (!startRange) return;
      panTouch = { startX: t.clientX, startY: t.clientY, startRange, direction: 'pending' };
    };

    const onPricePanTouchMove = (e: TouchEvent) => {
      if (!panTouch || e.touches.length !== 1) return;
      const t = e.touches[0];
      const xOffset = Math.abs(t.clientX - panTouch.startX);
      const yOffset = Math.abs(t.clientY - panTouch.startY);

      if (panTouch.direction === 'pending') {
        if (xOffset + yOffset < 5) return;
        panTouch.direction = yOffset >= xOffset * 0.5 ? 'vertical' : 'horizontal';
      }
      if (panTouch.direction === 'horizontal') return;

      e.preventDefault();
      e.stopPropagation();

      const ic = (chartRef.current as unknown as InternalChart);
      const yAxis = ic?._candlePane?.getAxisComponent();
      const size = (chartRef.current as unknown as ChartWithSize)?.getSize?.('candle_pane', DomPosition.Root);
      if (!yAxis || !size?.height) return;

      const { startRange } = panTouch;
      const distance = t.clientY - panTouch.startY;
      const scale    = distance / size.height;
      const difRange = startRange.range * scale;
      const newFrom  = startRange.from + difRange;
      const newTo    = startRange.to + difRange;
      const newRealFrom = yAxis.convertToRealValue(newFrom);
      const newRealTo   = yAxis.convertToRealValue(newTo);

      yAxis.setRange({
        from:      newFrom,
        to:        newTo,
        range:     newTo - newFrom,
        realFrom:  newRealFrom,
        realTo:    newRealTo,
        realRange: newRealTo - newRealFrom,
      });
      ic.adjustPaneViewport(false, true, true, true);
    };
    const onPricePanTouchEnd = () => { panTouch = null; };
    containerRef.current.addEventListener('touchstart', onPricePanTouchStart, { capture: true, passive: true });
    containerRef.current.addEventListener('touchmove', onPricePanTouchMove, { capture: true, passive: false });
    containerRef.current.addEventListener('touchend', onPricePanTouchEnd, { capture: true });
    containerRef.current.addEventListener('touchcancel', onPricePanTouchEnd, { capture: true });

    // ── Force chart redraw whenever vol label offsets/visibility change ─────
    // `scrollByDistance(0, false)` is a no-op scroll that reliably triggers
    // klinecharts' internal draw cycle, so the VOL_LABELS indicator re-draws
    // with the updated store state (offsets / hidden map).
    type ChartScroll = Chart & { scrollByDistance: (d: number, a?: boolean) => void };
    let prevOffsets = useChartStore.getState().volumeLabelOffsets;
    let prevHidden  = useChartStore.getState().volumeLabelHidden;
    const unsubVolRedraw = useChartStore.subscribe((s) => {
      if (s.volumeLabelOffsets !== prevOffsets || s.volumeLabelHidden !== prevHidden) {
        prevOffsets = s.volumeLabelOffsets;
        prevHidden  = s.volumeLabelHidden;
        try { (chart as unknown as ChartScroll).scrollByDistance(0, false); } catch { /* ignore */ }
      }
    });

    const container = containerRef.current;
    return () => {
      unsubVolRedraw();
      if (liveIntervalRef.current !== null) {
        clearInterval(liveIntervalRef.current);
        liveIntervalRef.current = null;
      }
      container?.removeEventListener('pointerdown', onCandlePanePointerDown, { capture: true });
      container?.removeEventListener('pointerdown', onVolLabelPointerDown, { capture: true });
      container?.removeEventListener('pointermove', onVolLabelPointerMove, { capture: true });
      container?.removeEventListener('pointerup', onVolLabelPointerUp, { capture: true });
      container?.removeEventListener('pointercancel', onVolLabelPointerUp, { capture: true });
      container?.removeEventListener('pointerleave', onVolLabelPointerLeave as EventListener, { capture: true });
      container?.removeEventListener('wheel', onWheel, { capture: true });
      container?.removeEventListener('touchstart', onYAxisTouchStart, { capture: true });
      container?.removeEventListener('touchmove', onYAxisTouchMove, { capture: true });
      container?.removeEventListener('touchend', onYAxisTouchEnd, { capture: true });
      container?.removeEventListener('touchcancel', onYAxisTouchEnd, { capture: true });
      container?.removeEventListener('touchstart', onPricePanTouchStart, { capture: true });
      container?.removeEventListener('touchmove', onPricePanTouchMove, { capture: true });
      container?.removeEventListener('touchend', onPricePanTouchEnd, { capture: true });
      container?.removeEventListener('touchcancel', onPricePanTouchEnd, { capture: true });
      cursorStyleEl.remove();
      setChart(null);
      setCrosshair(null);
      setCrosshairAxisY(null);
      setLastBar(null);
      if (container) dispose(container);
      chartRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync theme styles — then re-apply persisted user settings so colours/grid/etc survive
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    chart.setStyles(buildStyles(theme === 'dark'));
    applyPersistedChartSettings(chart);
    applyPersistedCandleType(chart);
  }, [theme]);

  // Force a resize pass when the right panel drawer opens/closes.
  // This covers the case where flex reflow doesn't trigger ResizeObserver
  // (or fires before the final width settles) and klinecharts keeps stale
  // pane bounds for hit-testing in Area/HLC modes.
  useEffect(() => {
    const chart = chartRef.current;
    const wrapperEl = wrapperRef.current;
    if (!chart || !wrapperEl) return;

    const containerEl = containerRef.current;
    if (containerEl) containerEl.style.height = `${wrapperEl.clientHeight}px`;

    chart.resize();
    requestAnimationFrame(() => {
      const c = chartRef.current;
      if (!c) return;
      c.resize();
      useChartStore.getState().refreshPaneTops();
      useChartStore.getState().refreshPricePaneHeight();
    });
  }, [rightToolbarOpen, rightPanelActive]);

  // Re-apply VOL colours whenever the user changes them via the settings modal
  const volColors = useChartStore((s) => s.indicatorColors['VOL']);
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    applyVolStyles(chart);
  }, [volColors]);

  // Apply price axis scale type — skipped when compare mode is active (compare forces Percentage)
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart || compareSymbols.length > 0) return;
    const type =
      priceScaleType === 'log'        ? YAxisType.Log
      : priceScaleType === 'percentage' ? YAxisType.Percentage
      : YAxisType.Normal;
    chart.setStyles({ yAxis: { type } });
    // Force a resize to ensure KLineCharts fully recalculates the y-axis ticks
    // after switching between log and normal (v9 sometimes skips the redraw).
    // Defer by one frame so the style change is fully processed first.
    requestAnimationFrame(() => chart.resize());
  }, [priceScaleType, compareSymbols]);

  // When indicators are toggled, refresh the legend values one frame later
  // (klinecharts needs one render cycle to materialise the new indicator result)
  const indicatorPaneMap = useChartStore((s) => s.indicatorPaneMap);
  useEffect(() => {
    if (!chartRef.current) return;
    requestAnimationFrame(() => {
      if (chartRef.current) {
        if (chartRef.current) {
          useChartStore.getState().setIndicatorValues(readIndValues(chartRef.current, getRightmostVisibleIndex(chartRef.current)));
        }
      }
    });
  }, [indicatorPaneMap]);

  // Force EVENT_MARKERS indicator to redraw when showEvents toggles
  const showEvents = useChartStore((s) => s.showEvents);
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    try {
      chart.overrideIndicator({ name: 'EVENT_MARKERS' }, 'candle_pane');
    } catch { /* ignore */ }
  }, [showEvents]);

  // ── Macro overlay pane lifecycle ────────────────────────────────────────────
  // Subscribe to pinnedSeries changes: create/remove/toggle panes for each series.
  const pinnedMacroSeries = useMacroStore((s) => s.pinnedSeries);
  const macroSeriesData   = useMacroStore((s) => s.seriesData);
  const seriesMeta        = useMacroStore((s) => s.seriesMeta);

  // Track which seriesIds have already been created as panes in the current chart instance
  const macroCreatedRef = useRef<Set<string>>(new Set());
  // Remember previous pinned series so we only touch pane heights on real visibility changes
  const prevPinnedRef = useRef<{ seriesId: string; visible: boolean }[]>([]);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    type ChartFull = Chart & {
      createIndicator: (name: string, isStack: boolean, opts: { id: string; height: number; minHeight: number }) => string;
      removeIndicator:  (paneId: string, name: string) => void;
      overrideIndicator: (o: { name: string; styles?: Record<string, unknown> }, paneId?: string) => void;
      setPaneOptions: (opts: { id: string; height?: number; visible?: boolean }) => void;
    };
    const c = chart as unknown as ChartFull;

    const currentIds  = new Set(pinnedMacroSeries.map((s) => s.seriesId));
    const createdIds  = macroCreatedRef.current;
    const prevPinned  = prevPinnedRef.current;
    let paneMapChanged = false;
    const nextPaneMap = new Map(useChartStore.getState().indicatorPaneMap);

    // Remove panes for series that were unpinned
    for (const id of [...createdIds]) {
      if (!currentIds.has(id)) {
        try { c.removeIndicator(macroPaneId(id), macroIndName(id)); } catch { /* ignore */ }
        delete (window as unknown as Record<string, unknown>)[macroWindowKey(id)];
        createdIds.delete(id);
        if (nextPaneMap.has(macroIndName(id))) {
          nextPaneMap.delete(macroIndName(id));
          paneMapChanged = true;
        }
      }
    }

    let anyCreated = false;
    let visibilityChanged = false;

    // Create / update panes for each pinned series
    for (const { seriesId, color, visible } of pinnedMacroSeries) {
      // Inject window data (may be empty array if not loaded yet)
      const data = macroSeriesData[seriesId] ?? [];
      (window as unknown as Record<string, unknown>)[macroWindowKey(seriesId)] = data;

      const meta = useMacroStore.getState().seriesMeta.find((m) => m.series_id === seriesId);
      const parts = seriesId.split('_');
      const shortName = parts.length > 1 ? parts.slice(1).join(' ') : seriesId;
      const unit = data[0]?.unit ?? null;
      const displayName = meta?.name ?? shortName;

      if (!createdIds.has(seriesId)) {
        // Only create the pane once both data and metadata are available.
        // This ensures the template is registered with the correct frequency
        // and MA figures from the start, avoiding stale-calc issues.
        if (data.length > 0 && meta) {
          registerMacroIndicator(seriesId, color, displayName, unit, meta.frequency);
          try {
            c.createIndicator(macroIndName(seriesId), false, {
              id: macroPaneId(seriesId),
              height: 100,
              minHeight: 60,
            });
            if (!visible) {
              try { c.overrideIndicator({ name: macroIndName(seriesId), visible: false }, macroPaneId(seriesId)); } catch { /* ignore */ }
              try { c.setPaneOptions({ id: macroPaneId(seriesId), height: 28, minHeight: 24 }); } catch { /* ignore */ }
            }
          } catch { /* ignore duplicate */ }
          createdIds.add(seriesId);
          nextPaneMap.set(macroIndName(seriesId), macroPaneId(seriesId));
          paneMapChanged = true;
          anyCreated = true;
        }
      } else {
        // Already created — update shortName, figures, data and trigger recalc.
        // Preserve the current visibility so a data refresh doesn't re-show a hidden pane.
        if (data.length > 0) {
          try {
            c.overrideIndicator({
              name: macroIndName(seriesId),
              shortName: displayName,
              figures: buildMacroFigures(displayName, unit, meta?.frequency),
              styles: { lines: buildMacroLineStyles(color, meta?.frequency) },
              calc: createMacroCalc(seriesId, meta?.frequency),
              calcParams: [data.length, meta?.frequency ? 1 : 0],
              visible,
            }, macroPaneId(seriesId));
          } catch { /* ignore */ }
          // Force canvas repaint so the line appears when data becomes available
          requestAnimationFrame(() => {
            const chartResizable = c as unknown as { resize?: () => void };
            chartResizable.resize?.();
          });
        }
        // Sync visibility ONLY when it actually changed (same pattern as ChartLegend for MACD/RSI)
        const prevVis = prevPinned.find((p) => p.seriesId === seriesId)?.visible;
        if (prevVis !== visible) {
          try { c.overrideIndicator({ name: macroIndName(seriesId), visible }, macroPaneId(seriesId)); } catch { /* ignore */ }
          try { c.setPaneOptions({ id: macroPaneId(seriesId), height: visible ? 80 : 28, minHeight: visible ? 60 : 24 }); } catch { /* ignore */ }
          visibilityChanged = true;
        }
      }
    }

    if (paneMapChanged) {
      useChartStore.getState().setIndicatorPaneMap(nextPaneMap);
    }
    if (anyCreated) {
      // Defer rebalance one frame so klinecharts finishes creating the pane(s)
      // before we measure and redistribute heights — same pattern used for
      // restoring regular indicators after template load.
      requestAnimationFrame(() => {
        useChartStore.getState().rebalancePanes();
      });
    } else if (visibilityChanged) {
      // Force KLineCharts to re-measure pane geometry so the title bar and
      // our floating legend rows land in the correct positions.
      (chart as unknown as { resize?: () => void }).resize?.();
      requestAnimationFrame(() => {
        useChartStore.getState().refreshPaneTops();
        useChartStore.getState().refreshPricePaneHeight();
      });
    }

    prevPinnedRef.current = pinnedMacroSeries.map((s) => ({ seriesId: s.seriesId, visible: s.visible }));
  }, [pinnedMacroSeries, macroSeriesData, seriesMeta]);

  // Cleanup all macro panes on chart unmount (handled by dispose() below,
  // but also clear createdIds so they'd be recreated on re-mount).
  useEffect(() => {
    const created = macroCreatedRef.current;
    return () => { created.clear(); };
  }, []);

  // Re-apply persisted chart settings whenever they change from external sources (server sync, localStorage)
  const candleUpColor      = useChartStore((s) => s.candleUpColor);
  const candleDownColor    = useChartStore((s) => s.candleDownColor);
  const hlcUpColorStore    = useChartStore((s) => s.hlcUpColor);
  const hlcDownColorStore  = useChartStore((s) => s.hlcDownColor);
  const showHGridStore     = useChartStore((s) => s.showHGrid);
  const showVGridStore     = useChartStore((s) => s.showVGrid);
  const showPriceMarkHighStore = useChartStore((s) => s.showPriceMarkHigh);
  const showPriceMarkLowStore  = useChartStore((s) => s.showPriceMarkLow);
  const showLastPriceLineStore = useChartStore((s) => s.showLastPriceLine);
  const showCrosshairStore = useChartStore((s) => s.showCrosshair);
  const yAxisReverseStore  = useChartStore((s) => s.yAxisReverse);
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    applyPersistedChartSettings(chart);
  }, [candleUpColor, candleDownColor, hlcUpColorStore, hlcDownColorStore, showHGridStore, showVGridStore, showPriceMarkHighStore, showPriceMarkLowStore, showLastPriceLineStore, showCrosshairStore, yAxisReverseStore]);

  // ── Comparison overlays ──────────────────────────────────────────────────
  // YAxisType.Percentage converts every value in the pane as:
  //   displayPct = (rawValue - mainFromClose) / mainFromClose * 100
  // So the compare calc must return a REBASED RAW PRICE:
  //   rebasedPrice = comparePrice * (mainFromClose / compareAnchorPrice)
  // At the anchor bar: rebasedPrice = mainFromClose → displayPct = 0% ✓
  // calcParams: [anchorTs (unix seconds), mainFromClose (raw price)]
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    type KBar = { timestamp: number; close: number };
    type ChartV9 = Chart & {
      createIndicator: (
        value: string | { name: string; calcParams?: number[] },
        isStack: boolean,
        opts: { id?: string },
      ) => string;
      removeIndicator: (paneId: string, name?: string) => void;
      getVisibleRange: () => { from: number; to: number };
      getDataList: () => KBar[];
      subscribeAction: (type: string, cb: () => void) => void;
      unsubscribeAction: (type: string, cb: () => void) => void;
      overrideIndicator: (o: { name: string; calcParams: number[] }, paneId: string) => void;
    };
    const c = chart as unknown as ChartV9;

    // Captured once per effect run — see compareGenerationRef's declaration
    // for why this can't reuse loadGenerationRef.
    const compareGen = ++compareGenerationRef.current;

    // Switch main Y-axis to percentage mode when comparing; restore user type otherwise.
    if (compareSymbols.length > 0) {
      chart.setStyles({ yAxis: { type: YAxisType.Percentage } });
    } else {
      const savedType = useChartStore.getState().priceScaleType;
      const restoredType =
        savedType === 'log'        ? YAxisType.Log
        : savedType === 'percentage' ? YAxisType.Percentage
        : YAxisType.Normal;
      chart.setStyles({ yAxis: { type: restoredType } });
    }
    chart.resize();

    // Remove all previous compare indicators (up to max 20)
    for (let i = 0; i < 20; i++) {
      try { c.removeIndicator('candle_pane', `COMPARE_${i}`); } catch { /* ignore */ }
      delete (window as unknown as Record<string, unknown>)[`__compare_COMPARE_${i}`];
    }

    if (!compareSymbols.length) return;

    // Capture initial anchor = leftmost visible bar
    const dataList = c.getDataList();
    const range = c.getVisibleRange();
    const fromBar = dataList[range.from];
    const initialAnchorTs = fromBar ? Math.floor(fromBar.timestamp / 1000) : 0;
    const initialMainFromClose = fromBar?.close ?? 0;

    // Sync initial anchor to store so ChartLegend can compute compare %
    if (initialAnchorTs) setCompareAnchorTs(initialAnchorTs);

    compareSymbols.forEach((sym, idx) => {
      const color = COMPARE_COLORS_EXPORT[idx % COMPARE_COLORS_EXPORT.length];
      const indicatorName = `COMPARE_${idx}`;

      // Register indicator.
      // calcParams[0] = anchorTs, calcParams[1] = mainFromClose at that bar.
      // Returns REBASED raw prices so klinecharts' YAxisType.Percentage works correctly.
      registerIndicator({
        name: indicatorName,
        shortName: sym,
        series: IndicatorSeries.Normal,
        figures: [{ key: 'pct', title: `${sym}: `, type: 'line' }],
        styles: {
          lines: [{ color, size: 1.5, smooth: false, style: LineType.Solid, dashedValue: [2, 2] }],
        },
        // Mutate a pre-rendered HTML element (compareLabelsRef[idx]) each frame so the
        // coloured % pill always tracks the RIGHTMOST VISIBLE bar and sits inside the
        // Y-axis strip (the indicator draw canvas is clipped to the chart pane and
        // cannot reach the Y-axis widget).
        draw: (params: {
          indicator: { result: { pct?: number | null }[]; calcParams: number[] };
          visibleRange: { from: number; to: number };
          bounding: { top: number };
          yAxis: { convertToPixel: (v: number) => number };
        }) => {
          const { indicator, visibleRange, bounding, yAxis } = params;
          const el = compareLabelsRef.current[idx];
          const toIdx = Math.min(visibleRange.to, indicator.result.length - 1);
          const toResult = indicator.result[toIdx];
          if (!toResult || toResult.pct == null) {
            if (el) el.style.display = 'none';
            return false;
          }
          const mainFromClose = indicator.calcParams[1] ?? 0;
          if (!mainFromClose) {
            if (el) el.style.display = 'none';
            return false;
          }
          // rebasedPrice = comparePrice * (mainFromClose / anchorPrice)
          // percentage vs anchor = (rebasedPrice / mainFromClose − 1) × 100
          const pct = (toResult.pct / mainFromClose - 1) * 100;
          const y = yAxis.convertToPixel(toResult.pct);
          if (el) {
            el.textContent = `${pct >= 0 ? '+' : ''}${pct.toFixed(2)}%`;
            el.style.top = `${bounding.top + y}px`;
            el.style.display = 'block';
          }
          return false;
        },
        // Show the actual compare price in the tooltip, not the internal rebased value.
        // Also clears calcParamsText so the anchor timestamp/price aren't shown.
        createTooltipDataSource: ({ kLineDataList, crosshair, indicator }: {
          kLineDataList: KBar[];
          crosshair: { dataIndex?: number };
          indicator: { calcParams: number[] };
        }) => {
          // Suppress canvas label — ChartLegend renders the compare row with live price + × button
          void kLineDataList; void crosshair; void indicator;
          return { name: '', calcParamsText: '', icons: [], values: [] };
        },
        calc: (klineData: KBar[], indicator: { calcParams: number[] }) => {
          const compareData: Map<number, number> =
            (window as unknown as Record<string, unknown>)[`__compare_${indicatorName}`] as Map<number, number>
            ?? new Map();
          const anchorTs: number = indicator.calcParams[0] ?? 0;
          const mainFromClose: number = indicator.calcParams[1] ?? 0;

          if (!compareData.size || !klineData.length || !anchorTs || !mainFromClose) {
            return klineData.map(() => ({ pct: null }));
          }

          // Compare symbol's price at anchor — needed to compute the rebase scale
          const compareAnchorPrice = compareData.get(anchorTs) ?? null;
          if (!compareAnchorPrice) return klineData.map(() => ({ pct: null }));

          // scale = mainFromClose / compareAnchorPrice
          // rebasedPrice = comparePrice * scale
          // → at anchor: rebasedPrice = mainFromClose → klinecharts shows 0%
          const scale = mainFromClose / compareAnchorPrice;

          return klineData.map((bar) => {
            const ts = Math.floor(bar.timestamp / 1000);
            const price = compareData.get(ts);
            if (price == null) return { pct: null };
            return { pct: price * scale };
          });
        },
      });

      // Create with initial anchor
      try {
        c.createIndicator({ name: indicatorName, calcParams: [initialAnchorTs, initialMainFromClose] }, true, { id: 'candle_pane' });
      } catch {
        try { c.createIndicator(indicatorName, true, { id: 'candle_pane' }); } catch { /* ignore */ }
      }

      // Fetch compare data, store in window, then trigger recalc with current anchor
      api.ohlcv(sym, timeframe, 2000, undefined, undefined, priceAdjustments).then((res) => {
        // A newer compare-set change (add/remove/reorder) superseded this
        // fetch while it was in flight — writing it now would land in a
        // window.__compare_COMPARE_{idx} slot the new set may be using for
        // a different symbol.
        if (compareGen !== compareGenerationRef.current) return;
        const map = new Map<number, number>();
        res.data.forEach((bar) => { map.set(bar.time, bar.close); });
        (window as unknown as Record<string, unknown>)[`__compare_${indicatorName}`] = map;

        const currentRange = c.getVisibleRange();
        const currentData = c.getDataList();
        const currentBar = currentData[currentRange.from];
        const ts = currentBar ? Math.floor(currentBar.timestamp / 1000) : initialAnchorTs;
        const fromClose = currentBar?.close ?? initialMainFromClose;
        try { c.overrideIndicator({ name: indicatorName, calcParams: [ts, fromClose] }, 'candle_pane'); } catch { /* ignore */ }
      }).catch(() => {});
    });

    // Re-anchor on scroll.
    // overrideIndicator triggers a full indicator recalc over all bars, so calling
    // it on every scroll event (which fires on every pixel of drag) freezes the UI.
    // Debounce: only recalc 150 ms after the user stops scrolling.
    let lastAnchorTs = initialAnchorTs;
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;

    const handleRangeChange = () => {
      if (debounceTimer !== null) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        debounceTimer = null;
        const currentData = c.getDataList();
        const currentRange = c.getVisibleRange();
        const currentBar = currentData[currentRange.from];
        if (!currentBar) return;
        const newAnchorTs = Math.floor(currentBar.timestamp / 1000);
        if (newAnchorTs === lastAnchorTs) return;
        lastAnchorTs = newAnchorTs;
        const newMainFromClose = currentBar.close;
        setCompareAnchorTs(newAnchorTs);
        compareSymbols.forEach((_, i) => {
          try {
            c.overrideIndicator({ name: `COMPARE_${i}`, calcParams: [newAnchorTs, newMainFromClose] }, 'candle_pane');
          } catch { /* ignore */ }
        });
      }, 150);
    };

    c.subscribeAction('onVisibleRangeChange', handleRangeChange);

    const labelsSnapshot = compareLabelsRef.current;
    return () => {
      if (debounceTimer !== null) clearTimeout(debounceTimer);
      // Restore user-chosen scale type when compare mode is turned off
      const savedType = useChartStore.getState().priceScaleType;
      const restoredType =
        savedType === 'log'        ? YAxisType.Log
        : savedType === 'percentage' ? YAxisType.Percentage
        : YAxisType.Normal;
      chart.setStyles({ yAxis: { type: restoredType } });
      chart.resize();
      c.unsubscribeAction('onVisibleRangeChange', handleRangeChange);
      // Hide HTML label pills left over from this compare set
      labelsSnapshot.forEach((el) => { if (el) el.style.display = 'none'; });
      for (let i = 0; i < compareSymbols.length; i++) {
        delete (window as unknown as Record<string, unknown>)[`__compare_COMPARE_${i}`];
      }
    };
  }, [compareSymbols, timeframe, setCompareAnchorTs, priceAdjustments]);

  // When symbol or timeframe changes: skip first mount, then reload fresh data.
  useEffect(() => {
    // ── Y-axis hover mouse tracking ───────────────────────────────────────────
    // Show an amber "+" pill when the cursor is in the rightmost Y-axis zone
    // (last 60 px of the price pane row). Click opens the alert create modal.
    const Y_ZONE = 62; // px from wrapper right edge to cover the Y-axis strip
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const onMouseMove = (e: MouseEvent) => {
      const chart = chartRef.current;
      const rect = wrapper.getBoundingClientRect();
      const x    = e.clientX - rect.left;
      const y    = e.clientY - rect.top;

      // ── Event marker hover detection ──────────────────────────────────────
      // Check if cursor is near a rendered event marker triangle (hit radius 8px)
      const HIT_X = 8; // horizontal hit radius in px
      let hitLayout = null;
      for (const layout of eventMarkerLayouts) {
        if (
          Math.abs(x - layout.x) <= HIT_X &&
          y >= layout.topY - 4 &&
          y <= layout.bottomY + 4
        ) {
          hitLayout = layout;
          break;
        }
      }
      if (hitLayout) {
        cancelTooltipClear();
        setHoveredEvents(hitLayout.events, { x: hitLayout.x, y: hitLayout.topY });
      } else {
        scheduleTooltipClear();
      }

      // ── O'Neil sell-signal marker hover detection ──────────────────────────
      let hitSellLayout = null;
      for (const layout of sellMarkerLayouts) {
        if (
          Math.abs(x - layout.x) <= HIT_X &&
          y >= layout.topY - 4 &&
          y <= layout.bottomY + 4
        ) {
          hitSellLayout = layout;
          break;
        }
      }
      if (hitSellLayout) {
        cancelSellTooltipClear();
        setHoveredSellEvents(
          hitSellLayout.events,
          { x: hitSellLayout.x, y: hitSellLayout.topY },
          formatSellMarkerDate(hitSellLayout.timestamp),
        );
      } else {
        scheduleSellTooltipClear();
      }

      if (!chart) { setYAxisHoverPrice(null); return; }
      if (useChartStore.getState().rightPanelActive !== 'alerts') { setYAxisHoverPrice(null); return; }
      if (x < rect.width - Y_ZONE) { setYAxisHoverPrice(null); return; }
      try {
        const result = (chart as unknown as {
          convertFromPixel: (coords: Array<{ x: number; y: number }>, finder: { paneId: string }) => Array<{ value?: number }>
        }).convertFromPixel([{ x, y }], { paneId: 'candle_pane' });
        const price = result?.[0]?.value;
        if (price != null && price > 0) {
          const rounded = price < 100
            ? Math.round(price * 10) / 10          // step 0.1
            : Math.round(price * 4)  / 4;          // step 0.25
          setYAxisHoverPrice({ y: e.clientY - rect.top, price: rounded });
        } else {
          setYAxisHoverPrice(null);
        }
      } catch {
        setYAxisHoverPrice(null);
      }
    };
    const onMouseLeave = () => {
      setYAxisHoverPrice(null);
      scheduleTooltipClear();
      scheduleSellTooltipClear();
    };
    wrapper.addEventListener('mousemove', onMouseMove);
    wrapper.addEventListener('mouseleave', onMouseLeave);

    // Tap-to-pin uses pointerdown/pointerup instead of 'click': klinecharts'
    // own touch handling calls preventDefault() on touchend for every tap
    // (to stop its internal tap handler from double-firing via the browser's
    // synthetic mouse-compat events), which suppresses the 'click' event that
    // would normally follow on mobile. pointerup fires before that touchend
    // handling and isn't part of the suppressed mouse-compat chain, so it
    // reaches us on both touch and mouse.
    let tapStart: { x: number; y: number; pointerId: number } | null = null;
    const TAP_MOVE_SLOP = 6; // px of movement still treated as a tap, not a pan

    const onWrapperPointerDown = (e: PointerEvent) => {
      if (!e.isPrimary) return;
      if ((e.target as HTMLElement)?.closest?.('[data-chart-tooltip]')) return;
      tapStart = { x: e.clientX, y: e.clientY, pointerId: e.pointerId };
    };

    const onWrapperPointerUp = (e: PointerEvent) => {
      // Let taps inside a pinned tooltip (e.g. the "View Announcement PDF" link)
      // reach their own click handler instead of being treated as a tap outside
      // the marker, which would unpin (and unmount) the tooltip before the
      // browser dispatches the click that follows pointerup on mobile.
      if ((e.target as HTMLElement)?.closest?.('[data-chart-tooltip]')) return;
      if (!tapStart || e.pointerId !== tapStart.pointerId) return;
      const { x: startX, y: startY } = tapStart;
      tapStart = null;
      if (Math.abs(e.clientX - startX) + Math.abs(e.clientY - startY) > TAP_MOVE_SLOP) return;

      const rect = wrapper.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const HIT_X = 8;
      let hitLayout = null;
      for (const layout of eventMarkerLayouts) {
        if (Math.abs(x - layout.x) <= HIT_X && y >= layout.topY - 4 && y <= layout.bottomY + 4) {
          hitLayout = layout;
          break;
        }
      }
      if (hitLayout) {
        setPinnedEvents(hitLayout.events, { x: hitLayout.x, y: hitLayout.topY });
      } else {
        setPinnedEvents(null, null);
      }

      let hitSellLayout = null;
      for (const layout of sellMarkerLayouts) {
        if (Math.abs(x - layout.x) <= HIT_X && y >= layout.topY - 4 && y <= layout.bottomY + 4) {
          hitSellLayout = layout;
          break;
        }
      }
      if (hitSellLayout) {
        setPinnedSellEvents(
          hitSellLayout.events,
          { x: hitSellLayout.x, y: hitSellLayout.topY },
          formatSellMarkerDate(hitSellLayout.timestamp),
        );
      } else {
        setPinnedSellEvents(null, null);
      }
    };
    wrapper.addEventListener('pointerdown', onWrapperPointerDown);
    wrapper.addEventListener('pointerup', onWrapperPointerUp);

    return () => {
      wrapper.removeEventListener('mousemove', onMouseMove);
      wrapper.removeEventListener('mouseleave', onMouseLeave);
      wrapper.removeEventListener('pointerdown', onWrapperPointerDown);
      wrapper.removeEventListener('pointerup', onWrapperPointerUp);
    };
  }, [setHoveredEvents, setPinnedEvents, setHoveredSellEvents, setPinnedSellEvents]);

  // ── Alert horizontal lines ────────────────────────────────────────────────
  // Sync active price_target alert conditions as horizontalStraightLine overlays.
  const handleYAxisClick = useCallback(() => {
    if (yAxisHoverPrice == null) return;
    openAlertModal(yAxisHoverPrice.price, symbol);
  }, [yAxisHoverPrice, openAlertModal, symbol]);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    type ChartOverlay = Chart & {
      createOverlay: (
        value: { name: string; id?: string; lock?: boolean; points: Array<{ value: number }>; extendData?: unknown; styles?: Record<string, unknown> },
        paneId?: string,
      ) => string | null;
      removeOverlay: (remove: { id: string }) => void;
    };
    const c = chart as unknown as ChartOverlay;

    // Remove all previously drawn alert overlays
    alertOverlayIdsRef.current.forEach((ids) => {
      ids.forEach((id) => { try { c.removeOverlay({ id }); } catch { /* ignore */ } });
    });
    alertOverlayIdsRef.current.clear();

    // Draw active, non-triggered, non-hidden price_target conditions for the current symbol
    const { hiddenAlertIds: hiddenIds } = useAlertStore.getState();
    const activeAlerts = alerts.filter(
      (a: Alert) => a.symbol === symbol && a.active && !a.triggered && !hiddenIds.has(a.id),
    );
    for (const alert of activeAlerts) {
      const ids: string[] = [];
      for (const cond of alert.conditions) {
        if (cond.type === 'price_target') {
          const oid = `alert_${alert.id}_${cond.value}`;
          // Format the price label matching step precision
          const label = cond.value < 100
            ? Number(cond.value).toFixed(1)
            : Number(cond.value).toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              });
          try {
            c.createOverlay({
              name:       'alertPriceLine',
              id:         oid,
              lock:       true,
              points:     [{ value: cond.value }],
              extendData: { label },
            }, 'candle_pane');
            ids.push(oid);
          } catch { /* ignore */ }
        }
      }
      if (ids.length > 0) alertOverlayIdsRef.current.set(alert.id, ids);
    }
  }, [alerts, symbol, hiddenAlertIds]);

  // When symbol or timeframe changes: skip first mount, then reload fresh data.
  useEffect(() => {
    if (firstMountRef.current) {
      firstMountRef.current = false;
      return;
    }
    const chart = chartRef.current;
    if (!chart) return;

    // Clear any existing live poll for the previous symbol/timeframe
    if (liveIntervalRef.current !== null) {
      clearInterval(liveIntervalRef.current);
      liveIntervalRef.current = null;
    }

    // Reset infinite-scroll guards.
    // When a period/from-date is active, disable infinite scroll (data is range-bounded).
    fetchingMoreRef.current = false;
    hasMoreRef.current      = chartFromDate === null;

    setLoading(true);
    setError(null);
    chart.clearData();

    // A rapid double (or triple) symbol/timeframe switch re-runs this effect
    // before the previous run's request resolves. Captured after
    // incrementing, so any earlier in-flight load's callbacks can detect
    // they've been superseded and skip applying their (now-stale) data.
    const gen = ++loadGenerationRef.current;

    const sym        = symbol;
    const tf         = timeframe;
    const isIntraday = isIntradayResolution(tf);
    const since      = chartFromDate ?? undefined;
    // When a period is active, request enough bars to cover the period.
    const initialLimit = since !== undefined ? 2000 : 500;

    const adj = priceAdjustments;
    const doLoad = () =>
      isIntraday
        ? api.intraday(sym, tf as IntradayResolution, initialLimit)
        : api.ohlcv(sym, tf, initialLimit, undefined, undefined, adj);

    doLoad()
      .then((res) => {
        // Discard stale responses from a load superseded by a newer one for the
        // same or a different symbol/timeframe (e.g. chartFromDate/priceAdjustments
        // changing re-triggers this effect, or rapid A -> B -> C symbol switching).
        if (gen !== loadGenerationRef.current) return;
        chart.applyNewData(toKLineData(res.data));
        useChartStore.getState().setLoadedSymbol(sym);
        // Re-apply price scale type after data reload (applyNewData resets axis internals)
        {
          const { priceScaleType: sc, compareSymbols: cmp } = useChartStore.getState();
          if (cmp.length === 0) {
            const scaleType = sc === 'log' ? YAxisType.Log : sc === 'percentage' ? YAxisType.Percentage : YAxisType.Normal;
            chart.setStyles({ yAxis: { type: scaleType } });
          }
        }
        try {
          (chart as unknown as { overrideIndicator: (o: { name: string }, paneId?: string) => void })
            .overrideIndicator({ name: 'VOL_LABELS' }, 'vol_pane');
        } catch { /* ignore */ }
        setLoading(false);
        requestAnimationFrame(() => {
          type ChartExt = typeof chart & {
            setBarSpace: (s: number) => void;
            getVisibleRange: () => { from: number; to: number };
          };
          const chartExt = chart as ChartExt;
          if (since !== undefined) {
            // Find the first bar at or after the period start.
            // setBarSpace is sized so only the period's bars fill the pane width;
            // all historical bars remain loaded for correct indicator calculations.
            const periodStartIdx = res.data.findIndex((b) => b.time >= since);
            const visibleBars    = periodStartIdx >= 0 ? res.data.length - periodStartIdx : res.data.length;
            const totalBars      = res.data.length;
            const paneWidth      = (wrapperRef.current?.clientWidth ?? 800) - 60;
            // "All" is the one period whose since-sentinel is 0 (see periodToFromDate) —
            // its whole point is compressing the entire loaded history into view, so it
            // skips the 3px legibility floor that the day-level periods rely on to keep
            // individual candles distinguishable.
            const isAllPeriod = since === 0;
            const minBarSpace = isAllPeriod ? 1 : 3;
            if (visibleBars > 0) {
              // Initial guess — pane width here is only approximate (it doesn't know
              // klinecharts' real candle-pane pixel width, y-axis width, or its
              // fixed right-side offset), so this alone lands close but not exact.
              // Ceiling of 49 stays under klinecharts' own hard
              // BarSpaceLimitConstants.MAX (50) — setBarSpace silently no-ops above
              // that, which was the real cause of periods like 1M rendering far wider
              // than requested (the call was rejected outright, leaving barSpace stuck
              // at whatever it was before).
              const desired = Math.max(minBarSpace, Math.min(49, paneWidth / visibleBars));
              chartExt.setBarSpace(desired);
              // scrollToDataIndex must run in a SECOND RAF so klinecharts processes
              // the new barSpace before computing the scroll offset.
              requestAnimationFrame(() => {
                chart.scrollToDataIndex(totalBars - 1, 0);
                // Correction pass: read back how many bars klinecharts actually fit
                // at that barSpace and rescale once against the real pane width,
                // rather than the guessed one. This makes the period-start bar land
                // at the left edge regardless of axis width/offset assumptions.
                requestAnimationFrame(() => {
                  const range = chartExt.getVisibleRange();
                  const actualVisibleBars = totalBars - range.from;
                  if (actualVisibleBars > 0 && actualVisibleBars !== visibleBars) {
                    const corrected = Math.max(minBarSpace, Math.min(49, desired * (actualVisibleBars / visibleBars)));
                    chartExt.setBarSpace(corrected);
                    requestAnimationFrame(() => {
                      chart.scrollToDataIndex(totalBars - 1, 0);
                    });
                  }
                });
              });
            }
          } else {
            chartExt.setBarSpace(8);
            requestAnimationFrame(() => {
              chart.scrollToRealTime();
            });
          }
        });
        requestAnimationFrame(() => { useChartStore.getState().setIndicatorValues(readIndValues(chart, getRightmostVisibleIndex(chart))); });
        const lb = res.data.at(-1);
        if (lb) setLastBar({ time: lb.time, open: lb.open, high: lb.high, low: lb.low, close: lb.close, volume: lb.volume ?? 0 });

        // Start live-polling during CSE trading hours
        if (isCSEMarketOpen()) {
          let lastBarTs = res.data.at(-1)?.time ?? 0;
          liveIntervalRef.current = setInterval(async () => {
            try {
              if (sym !== symbolRef.current || tf !== timeframeRef.current) return;
              const liveRes = isIntraday
                ? await api.intraday(sym, tf as IntradayResolution, 60)
                : await api.ohlcv(sym, tf, 60, undefined, undefined, adj);
              if (sym !== symbolRef.current || tf !== timeframeRef.current) return;
              // Filter bars at or after the last known bar (handles both update and new-bar cases)
              const updatedBars = liveRes.data.filter((b) => b.time >= lastBarTs);
              updatedBars.forEach((b) => {
                (chart as unknown as { updateData: (d: KLineData) => void })
                  .updateData(toKLineData([b])[0]);
              });
              if (updatedBars.length > 0) {
                lastBarTs = updatedBars[updatedBars.length - 1].time;
                const latest = updatedBars[updatedBars.length - 1];
                setLastBar({ time: latest.time, open: latest.open, high: latest.high, low: latest.low, close: latest.close, volume: latest.volume ?? 0 });
                try {
                  (chart as unknown as { overrideIndicator: (o: { name: string }, paneId?: string) => void })
                    .overrideIndicator({ name: 'VOL_LABELS' }, 'vol_pane');
                } catch { /* ignore */ }
              }
            } catch { /* silently ignore; next tick will retry */ }
          }, 60_000);
        }
      })
      .catch((err: unknown) => {
        if (gen !== loadGenerationRef.current) return;
        setError(err instanceof Error ? err.message : 'Failed to load data');
        setLoading(false);
      });
  }, [symbol, timeframe, chartFromDate, setLastBar, priceAdjustments]);

  return (
    <div className="flex flex-col h-full bg-bg">
      <IndicatorBar chartRef={chartRef} />
      <div className="relative flex-1 min-h-0 overflow-hidden" ref={wrapperRef}>
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center z-chart-overlay pointer-events-none">
            <span className="text-muted text-xs">Loading…</span>
          </div>
        )}
        {error && (
          <div className="absolute inset-0 flex items-center justify-center z-chart-overlay pointer-events-none">
            <span className="text-[var(--color-down)] text-xs">Error: {error}</span>
          </div>
        )}
        {/* Watermark — centered within the price pane only (excludes VOL sub-pane). */}
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: pricePaneHeight ?? '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            pointerEvents: 'none',
            userSelect: 'none',
            overflow: 'hidden',
            color: theme === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.10)',
          }}
        >
          <span style={{ fontSize: '60px', fontWeight: 700, lineHeight: 1, letterSpacing: '-0.02em', fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif" }}>
            {symbol}
          </span>
          {symbolName && (
            <span style={{ fontSize: '24px', fontWeight: 400, lineHeight: 1.2, fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif" }}>
              {symbolName}
            </span>
          )}
          <span style={{ fontSize: '20px', fontWeight: 400, letterSpacing: '0.06em', lineHeight: 1, fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", marginTop: symbolName ? '2px' : '0' }}>
            CeylonCharts.com
          </span>
        </div>
        {/* touch-action: none — klinecharts' own touch-move handler never calls
            preventDefault() (it's commented out in the library source), so without
            this the mobile browser's native scroll/pinch-zoom wins the gesture
            before the chart's pan/zoom logic ever sees a clean drag. */}
        <div ref={containerRef} className="w-full h-full touch-none" />
        {/* Logo — bottom-left of price pane; favicon icon on mobile */}
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: pricePaneHeight ?? '100%',
            pointerEvents: 'none',
            overflow: 'hidden',
          }}
        >
          <img
            src={isMobile ? iconSrc : logoSrc}
            alt="CeylonCharts"
            draggable={false}
            style={{
              position: 'absolute',
              left: 12,
              bottom: 12,
              height: isMobile ? 28 : 38,
              width: 'auto',
              opacity: 0.45,
              filter: isMobile ? iconOutline : logoOutline,
              pointerEvents: 'none',
              zIndex: Z_INDEX.chartOverlay,
            }}
          />
        </div>
        <ChartLegend />
        <AxisValueLabels />
        <EventTooltip />
        <WilliamOneilSellTooltip />
        {/* Y-axis alert "+" hover pill — visible when cursor is in Y-axis zone */}
        {yAxisHoverPrice && rightPanelActive === 'alerts' && (
          <button
            onClick={handleYAxisClick}
            style={{
              position:    'absolute',
              right:       0,
              top:         yAxisHoverPrice.y,
              transform:   'translateY(-50%)',
              zIndex:      Z_INDEX.chartOverlay + 10,
              display:     'flex',
              alignItems:  'center',
              gap:         '2px',
              paddingLeft: '4px',
              paddingRight: '6px',
              paddingTop:   '2px',
              paddingBottom: '2px',
              background:   '#F59E0B',
              color:        '#000',
              fontSize:     '11px',
              fontWeight:   700,
              borderRadius: '3px 0 0 3px',
              whiteSpace:   'nowrap',
              cursor:       'pointer',
              userSelect:   'none',
              lineHeight:   1.2,
              pointerEvents: 'all',
            }}
            title="Click to set price alert"
          >
            <span style={{ fontSize: '14px', lineHeight: 1, marginRight: '1px' }}>+</span>
            {yAxisHoverPrice.price < 100
              ? yAxisHoverPrice.price.toFixed(1)
              : yAxisHoverPrice.price.toFixed(2)}
          </button>
        )}
        {/* Y-axis % pills for each compare symbol — positioned by the indicator draw callback */}
        {compareSymbols.map((sym, idx) => (
          <div
            key={sym}
            ref={(el) => { compareLabelsRef.current[idx] = el; }}
            data-compare-pill={idx}
            style={{
              position: 'absolute',
              right: 0,
              display: 'none',
              padding: '2px 5px',
              backgroundColor: COMPARE_COLORS_EXPORT[idx % COMPARE_COLORS_EXPORT.length],
              color: '#fff',
              fontSize: '11px',
              lineHeight: 1.2,
              borderRadius: '2px',
              transform: 'translateY(-50%)',
              pointerEvents: 'none',
              whiteSpace: 'nowrap',
              zIndex: Z_INDEX.chartOverlay,
              fontFamily: "'Helvetica Neue', sans-serif",
            }}
          />
        ))}
      </div>
      <PeriodBar />
    </div>
  );
}
